// ====================
// Azurechatbot.js - COMPLETE VERSION
// Includes user name management and full personalization
// ====================
// At the top of Azurechatbot.js, add:
import { SecureAPI } from '../config.js';
import { 
  AZURE_OPENAI_ENDPOINT, 
  AZURE_OPENAI_API_KEY as AZURE_OPENAI_KEY,
  AZURE_OPENAI_DEPLOYMENT as AZURE_DEPLOYMENT_NAME
} from '../azureConfigHelper';
// ====================
// AZURE CONFIGURATION
// ====================
let globalFirestoreService = null;

export const SAVE_MEMORY_TOOL = {
  type: "function",
  function: {
    name: "save_conversation_memory",
    description: "Save important information from the conversation for future reference. Use when user mentions: upcoming events, personal facts, ongoing situations, important dates, preferences, or context worth remembering.",
    parameters: {
      type: "object",
      properties: {
        memory: {
          type: "string",
          description: "Clear, concise summary of what to remember (e.g., 'User has biology exam on Nov 15th')"
        },
        topic: {
          type: "string",
          description: "Main category (e.g., 'exam', 'work', 'health', 'family', 'hobby')"
        },
        importance: {
          type: "string",
          enum: ["high", "medium", "low"],
          description: "How important this is for future conversations. High: critical events/facts. Medium: useful context. Low: minor details."
        },
        tags: {
          type: "array",
          items: { type: "string" },
          description: "Keywords for retrieval (e.g., ['education', 'exam', 'biology', 'november'])"
        },
        expiresAt: {
          type: "string",
          description: "ISO date when memory becomes irrelevant (optional, e.g., '2025-11-16' for day after exam)"
        },
        context: {
          type: "string",
          description: "Optional additional context or notes"
        }
      },
      required: ["memory", "topic"]
    }
  }
};
export const MEMORY_INSTRUCTIONS = `
🧠 CONVERSATION MEMORY - AUTOMATIC SAVING:

**WHEN TO SAVE MEMORIES:**
- User mentions upcoming events (exams, meetings, appointments, deadlines)
- User shares personal facts (job, school, hobbies, preferences)
- User discusses ongoing situations (projects, health issues, relationships)
- User mentions important dates or milestones
- User expresses preferences or dislikes

**WHAT TO SAVE:**
✅ "I have a biology exam next Friday" → save_conversation_memory
✅ "I'm working on a React project for work" → save_conversation_memory
✅ "My mom's birthday is December 10th" → save_conversation_memory
✅ "I've been dealing with back pain lately" → save_conversation_memory
✅ "I love Italian food but hate seafood" → save_conversation_memory

**WHAT NOT TO SAVE:**
❌ General questions ("What's the weather?")
❌ Casual greetings ("Hey, how are you?")
❌ Already-handled tasks ("Thanks for the flashcards")
❌ Temporary context that won't be useful later

**HOW TO USE SAVED MEMORIES:**
- When memory context appears in your system prompt, reference it naturally
- Example: If you see "[2 days ago] User has biology exam tomorrow"
  → User: "Hey!" 
  → You: "Hey! How did that biology exam go?"
- Don't force references - only when contextually appropriate
- Show continuity and care about the user's life

**MEMORY SAVING IS AUTOMATIC:**
- Call save_conversation_memory when you detect important info
- No need to tell user you're saving it
- Just save and continue the conversation naturally
`;


// ✅ STEP 1: Initialize Firestore Service
const initFirestoreService = async () => {
  try {
    const { default: FirestorePersistentStorageService } = await import('./services/FirestorePersistentStorageService');
    
    const service = new FirestorePersistentStorageService();
    await service.initialize();
    
    globalFirestoreService = service;
    window.firestoreService = service; // Also set on window for global access
    
    console.log('✅ Firestore service initialized for Azurechatbot');
    
    // ✅ Automatically load username after service is ready
    await loadUserNameFromFirestore();
    
    return service;
  } catch (error) {
    console.error('❌ Failed to initialize Firestore service:', error);
    return null;
  }
};

// ✅ STEP 2: Load Username from Firestore
const loadUserNameFromFirestore = async () => {
  try {
    const service = globalFirestoreService || window.firestoreService;
    
    if (!service?.isReady()) {
      console.log('⏳ Firestore service not ready yet');
      return null;
    }

    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.log('❌ No user authenticated');
      return null;
    }

    console.log('🔥 Loading username for:', currentUser.email);

    const settings = await service.getUserSettings();
    
    // ✅ PRIORITY: Use saved userName from Firestore
    if (settings?.userName && settings.userName.trim() !== '') {
      console.log('✅ Username loaded from Firestore:', settings.userName);
      
      // ✅ Update localStorage cache to match Firestore
      localStorage.setItem('preptune_user_name', JSON.stringify(settings.userName));
      
      return settings.userName;
    } else {
      // ✅ If no saved name, return null (AI will ask for it)
      console.log('⚠️ No username in Firestore - AI will ask for name');
      return null;
    }
  } catch (error) {
    console.error('❌ Failed to load username:', error);
    return null;
  }
};

// ✅ STEP 3: Setup Auth State Listener (call once on app init)
const setupAuthListener = async () => {
  try {
    const { getAuth, onAuthStateChanged } = await import('firebase/auth');
    const auth = getAuth();
    
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        console.log('🔄 Auth state changed, reloading username');
        await loadUserNameFromFirestore();
      } else {
        console.log('❌ User logged out');
        localStorage.removeItem('preptune_user_name');
      }
    });
  } catch (error) {
    console.error('Failed to setup auth listener:', error);
  }
};

// ✅ STEP 4: Call this when your app initializes
const initializeUsernameSystem = async () => {
  await initFirestoreService();
  await setupAuthListener();
};

/**
 * Updated system message to include new instructions
 */
const createUpdatedSystemMessage = (userName = null) => {
  const baseMessage = createSystemMessage(userName);
  
  // Replace old web search instructions with updated ones
  const updatedContent = baseMessage.content.replace(
    /🔍 WEB SEARCH - NATURAL DETECTION:[\s\S]*?(?=\n\n[🎙️📝🌤️📚📅⚡])/,
  );
  
  return {
    role: "system",
    content: updatedContent
  };
};

const NATURAL_CHAT_STYLE = `
🗣️ HOW TO TALK:
- NEVER use exclamation marks (!) in responses
GREETING FORMAT:
✅ CORRECT: "Hey, [name]" or "Hey [name]"
❌ WRONG: "Hey! [name]" or "Hey![name]"
- Chat like a real friend, not a robot or assistant
- Keep responses short and casual unless they need detail
- Use contractions: "I'll", "don't", "can't", "you're", "it's"
- Never use em dashes (—), use commas or periods instead
- Don't start messages with "I" too often
- Mix up how you start sentences
- Say "yeah" or "yep" sometimes instead of always "yes"
- Say "gonna", "wanna", "kinda" when it fits
- Don't be overly enthusiastic or use too many exclamation marks
- One exclamation mark max per message usually
- Don't say "Great question!" or "That's a great idea!" 
- Don't say "Absolutely!" or "Certainly!" or "Of course!"
- Don't say "I'd be happy to help" or "I'm here to help"
- Just help, don't announce that you're helping
- If something is simple, give a simple answer
- Don't over explain things
- Be chill, not corporate

BAD examples:
- "I'd be happy to help you with that!"
- "That's a great question — let me explain."
- "Absolutely! I can certainly assist with that."
- "Great choice! Here's what I recommend:"

GOOD examples:
- "Yeah, here's how to do that."
- "Oh nice, let me check that for you."
- "Sure thing. So basically..."
- "Hmm, looks like..."
- "Got it. Here's what I found."
`;
const NUMBER_FORMATTING_RULES = `
📊 NUMBER FORMATTING - CRITICAL:

ALWAYS write numbers naturally and correctly:

✅ CORRECT:
- "$250 million" (NOT "250 dollar million")
- "$1.5 billion" (NOT "1.5 dollar billion")
- "50%" (NOT "50 percent sign")
- "10,000 users" (NOT "10 thousand users")
- "$50,000" (NOT "50 thousand dollars")

❌ WRONG:
- "250 dollar million" → Should be "$250 million"
- "1.5 dollar billion" → Should be "$1.5 billion"
- "50 percent sign" → Should be "50%"

RULES:
1. Dollar sign BEFORE the number: $250
2. Million/billion AFTER the number: $250 million
3. Use commas for thousands: 10,000
4. Percent sign AFTER number: 50%
5. No spaces in currency: $50 not $ 50

EXAMPLES IN SENTENCES:
- "Tesla raised $250 million in funding"
- "The company is valued at $1.5 billion"
- "Sales increased by 50% this quarter"
- "They have over 10,000 active users"
`;

// ====================
// USER NAME STORAGE
// ====================

/**
 * Get user name from Firestore (with localStorage fallback for backwards compatibility)
 * @param {FirestorePersistentStorageService} firestoreService - The Firestore service instance
 * @returns {Promise<string|null>} User name or null
 */
/**
 * Get stored user name from Firestore only
 */

/**
 * ✅ FIXED: Wait for Firestore to be ready before checking username
 */
export const getStoredUserName = async (firestoreService = null) => {
  try {
    console.log('🔍 Getting user name - waiting for Firestore...');
    
    // ✅ Get the service (passed, global, or window)
    const service = firestoreService || globalFirestoreService || window.firestoreService;
    
    // ✅ WAIT for service to be ready (up to 5 seconds)
    if (service && !service.isReady()) {
      console.log('⏳ Firestore not ready yet, waiting...');
      
      for (let i = 0; i < 50; i++) { // 50 attempts × 100ms = 5 seconds max
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (service.isReady()) {
          console.log('✅ Firestore became ready!');
          break;
        }
      }
    }
    
    if (!service || !service.isReady || !service.isReady()) {
      console.log('⚠️ Firestore service still not ready after waiting');
      return null;
    }
    
    // ✅ Now fetch the username
    const settings = await service.getUserSettings();
    const name = settings?.userName;
    
    if (name && name.trim() !== '') {
      console.log('✅ Loaded name from Firestore:', name);
      
      // ✅ UPDATE localStorage to match Firestore
      localStorage.setItem('preptune_user_name', JSON.stringify(name));
      
      return name;
    }
    
    console.log('⚠️ No name found in Firestore');
    return null;
    
  } catch (error) {
    console.error('❌ Error reading name from Firestore:', error);
    return null;
  }
};
// Find the getStoredUserName function (around line 326) and REPLACE it with this:
export const getStoredUserNameSync = () => {
  try {
    // ✅ ONLY use localStorage as a CACHE (should already match Firestore)
    const stored = localStorage.getItem('preptune_user_name');
    if (stored) {
      const name = JSON.parse(stored);
      console.log('📦 Using cached name from localStorage:', name);
      return name;
    }
    return null;
  } catch (error) {
    console.error('Error reading cached name:', error);
    return null;
  }
};

// ============================================
// 🚀 USERNAME CACHE MANAGER
// ============================================

class UsernameCacheManager {
  constructor() {
    this.cache = {
      username: null,
      userId: null,
      lastUpdated: null,
      version: 1
    };
    this.listeners = [];
  }

  getUsername() {
    return this.cache.username;
  }

  getVersion() {
    return this.cache.version;
  }

async loadAndCache(firestoreService) {
  try {
    if (!firestoreService) {
      return null;
    }

    // ✅ CORRECT: Use getUserData() instead of getUserSettings()
    const userData = await firestoreService.getUserData();
    const newUsername = userData?.settings?.userName;

    const usernameChanged = this.cache.username !== newUsername;

    if (newUsername && newUsername.trim() !== '') {
      this.cache.username = newUsername;
      this.cache.lastUpdated = Date.now();

      if (usernameChanged) {
        console.log('✅ Username cached:', newUsername);
        this.cache.version++;
        this.notifyListeners('username_changed', newUsername);
      }

      return newUsername;
    }

    return null;

  } catch (error) {
    console.error('❌ Failed to load username:', error);
    return null;
  }
}
async saveAndUpdateCache(name, firestoreService) {
  if (!name || name.trim() === '') return false;

  const trimmedName = name.trim();

  try {
    // ✅ Use updateUserData (like SettingsModal does)
    if (firestoreService) {
      await firestoreService.updateUserData({
        'settings.userName': trimmedName
      });
    }

    const usernameChanged = this.cache.username !== trimmedName;
    this.cache.username = trimmedName;
    this.cache.lastUpdated = Date.now();

    if (usernameChanged) {
      this.cache.version++;
      this.notifyListeners('username_changed', trimmedName);
      console.log('✅ Username updated:', trimmedName);
    }

    return true;

  } catch (error) {
    console.error('❌ Error saving username:', error);
    return false;
  }
}

async clearAndInvalidate(firestoreService) {
  try {
    // ✅ Use updateUserData
    if (firestoreService) {
      await firestoreService.updateUserData({
        'settings.userName': null
      });
    }

    const wasSet = this.cache.username !== null;
    this.cache.username = null;
    this.cache.lastUpdated = Date.now();

    if (wasSet) {
      this.cache.version++;
      this.notifyListeners('username_cleared', null);
    }

    return true;

  } catch (error) {
    console.error('❌ Error clearing username:', error);
    return false;
  }
}

  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('Listener error:', error);
      }
    });
  }

  getCacheStatus() {
    return {
      username: this.cache.username,
      version: this.cache.version,
      lastUpdated: this.cache.lastUpdated ? new Date(this.cache.lastUpdated).toISOString() : null,
      isCached: this.cache.username !== null
    };
  }
}

const usernameCacheManager = new UsernameCacheManager();
window.usernameCacheManager = usernameCacheManager;
// ============================================
// ⚡ PARALLEL MEMORY & DATA LOADER
// ============================================

class ParallelDataLoader {
  constructor(firestoreService, memoryService) {
    this.firestoreService = firestoreService;
    this.memoryService = memoryService;
    this.lastLoadTime = 0;
    this.loadCache = null;
    this.cacheExpiry = 30000;
  }

  async loadUserContextInParallel(userMessage = null) {
    try {
      const loadStart = performance.now();
      console.log('⚡ Starting PARALLEL data load...');

      // ✅ LOAD BOTH AT THE SAME TIME
      const [username, memoryContext] = await Promise.all([
        this.loadUsernameOptimized(),
        this.loadMemoryOptimized(userMessage)
      ]);

      const loadTime = performance.now() - loadStart;
      console.log(`⚡ Parallel load completed in ${loadTime.toFixed(0)}ms`);

      return {
        username: username || null,
        memoryContext: memoryContext || null,
        loadTime
      };

    } catch (error) {
      console.error('❌ Parallel load error:', error);
      return {
        username: null,
        memoryContext: null,
        loadTime: 0
      };
    }
  }

  async loadUsernameOptimized() {
    try {
      // ✅ Check cache first (instant)
      const cachedUsername = usernameCacheManager.getUsername();
      if (cachedUsername) {
        console.log('📦 Username from cache (instant):', cachedUsername);
        return cachedUsername;
      }

      // Load from Firestore if not cached
      console.log('🔥 Loading username from Firestore...');
      const username = await usernameCacheManager.loadAndCache(this.firestoreService);
      return username;

    } catch (error) {
      console.error('❌ Username load error:', error);
      return null;
    }
  }

  async loadMemoryOptimized(userMessage) {
    try {
if (!this.memoryService) {
  return null;
}

      // ✅ Use cache if recently loaded
      const now = Date.now();
      if (this.loadCache && now - this.lastLoadTime < this.cacheExpiry) {
        console.log('📦 Memory from cache (recent load)');
        return this.loadCache;
      }

      // Load fresh memory
      console.log('🧠 Loading memory context...');
      const memoryContext = await this.memoryService.getMemorySummaryForAI(userMessage);

      if (memoryContext) {
        this.loadCache = memoryContext;
        this.lastLoadTime = now;
      }

      return memoryContext;

    } catch (error) {
      console.error('❌ Memory load error:', error);
      return null;
    }
  }

  invalidateMemoryCache() {
    this.loadCache = null;
    this.lastLoadTime = 0;
    console.log('🔄 Memory cache invalidated');
  }
}

/**
 * Check if user needs to provide name
 */
export const needsUserName = async (firestoreService = null) => {
  try {
    const storedName = await getStoredUserName(firestoreService);
    const needs = !storedName || storedName.trim() === '';
    console.log('🔍 needsUserName check:', { storedName, needs });
    return needs;
  } catch (error) {
    console.error('❌ Error checking if name needed:', error);
    return true;
  }
};
/**
 * Save user name to Firestore (and localStorage as backup)
 * @param {string} name - User's name
 * @param {FirestorePersistentStorageService} firestoreService - The Firestore service instance
 * @returns {Promise<boolean>} Success status
 */

export const saveUserName = async (name, firestoreService = null) => {
  if (!name || name.trim() === '') {
    console.warn('Cannot save empty name');
    return false;
  }

  const trimmedName = name.trim();

  try {
    console.log('💾 Saving user name:', trimmedName);
    
    const service = firestoreService || globalFirestoreService || window.firestoreService;
    
    if (!service || !service.isReady || !service.isReady()) {
      console.warn('⚠️ Could not save to Firestore - service not ready');
      return false;
    }
    
    // ✅ USE CACHE MANAGER TO SAVE
    return await usernameCacheManager.saveAndUpdateCache(trimmedName, service);
    
  } catch (error) {
    console.error('❌ Error saving name:', error);
    return false;
  }
};

/**
 * Clear stored user name from both Firestore and localStorage
 * @param {FirestorePersistentStorageService} firestoreService - The Firestore service instance
 * @returns {Promise<boolean>} Success status
 */
export const clearUserName = async (firestoreService = null) => {
  try {
    console.log('🗑️ Clearing user name');
    
    const service = firestoreService || globalFirestoreService || window.firestoreService;
    
    // ✅ USE CACHE MANAGER TO CLEAR
    return await usernameCacheManager.clearAndInvalidate(service);
    
  } catch (error) {
    console.error('❌ Error clearing name:', error);
    return false;
  }
};

/**
 * Get user name with fallback chain: Firestore -> localStorage -> Firebase Auth -> 'Guest'
 * @param {FirestorePersistentStorageService} firestoreService - The Firestore service instance
 * @returns {Promise<string>} User name (never null)
 */
/**
 * Get user name - ONLY from Firestore custom name
 */
export const getUserNameWithFallback = async (firestoreService = null) => {
  try {
    // ✅ ONLY get custom name from Firestore
    const storedName = await getStoredUserName(firestoreService);
    if (storedName && storedName.trim() !== '') {
      console.log('✅ Found custom username:', storedName);
      return storedName;
    }
    
    // ✅ Return null if no custom name (AI will ask for it)
    console.log('⚠️ No custom username - AI will ask');
    return null;
    
  } catch (error) {
    console.error('❌ Error getting user name:', error);
    return null;
  }
};

// ====================
// TOOL DEFINITIONS
// ====================

// User Name Collection Tool
const USER_NAME_TOOL = {
  type: "function",
  function: {
    name: "save_user_name",
    description: "Save the user's name when they first introduce themselves or tell you their name. Call this ONCE when you learn their name.",
    parameters: {
      type: "object",
      properties: {
        name: {
          type: "string",
          description: "The user's first name (capitalize first letter)"
        }
      },
      required: ["name"]
    }
  }
};


const NATURAL_FOLLOW_UP_INSTRUCTIONS = `
🎯 NATURAL FOLLOW-UP MESSAGES - CRITICAL:

After tools complete and display their cards/results, you MUST provide a separate, natural follow-up message:

WEATHER TOOL:
- Card shows weather data automatically
- Your message: Give helpful tip based on conditions (1-2 sentences)
  ✅ "Looks chilly! I'd grab a sweater—it drops to 45°F around 6pm."
  ✅ "Perfect day ahead! Sunny and 72°F all afternoon."
  ✅ "Rain's coming around 3pm, so bring an umbrella if you're heading out."
  ✅ "It's 67°F now but warming up to 75°F in the next hour. Check the card for hourly details!"
  ❌ DON'T repeat: temperature, condition, humidity (already in card)
  ❌ DON'T use user's name in every sentence

NEWS SEARCH:
- Card shows news articles automatically
- Your message: Brief acknowledgment + mention podcast option (1-2 sentences)
  ✅ "Just pulled the latest on tech! Hit the 'Generate News Podcast' button if you want to hear these stories."
  ✅ "Found 5 recent articles about AI. You can generate a podcast summary using the button on the card."
  ✅ "Here's what's happening with climate change. Want to listen instead? Use the podcast button above."
  ❌ DON'T repeat article titles/details (already in card)
  ❌ DON'T be robotic or use same phrases every time

FLASHCARDS:
- Card shows flashcard preview automatically
- Your message: Encouraging, supportive (1 sentence)
  ✅ "Good luck! Let me know if you need more flashcards on this topic."
  ✅ "All set! Take your time with these and reach out if you need anything else."
  ✅ "Challenge yourself and see how many you can get right!"
  ✅ "Ready to study! Feel free to ask for more if you want to keep going."
  ❌ DON'T repeat the topic or count (already shown in card)

QUIZ:
- Card shows quiz preview automatically
- Your message: Motivating, casual (1 sentence)
  ✅ "Good luck! See how well you know this topic."
  ✅ "Challenge time! Let me know if you need hints."
  ✅ "Ready when you are! Take your time and do your best."
  ✅ "All set! Feel free to ask for help if you get stuck."
  ❌ DON'T repeat question count or topic (already in card)

JOURNAL ENTRIES:
- After saving journal entry
- Your message: Thoughtful, supportive
  ✅ "Saved to your journal! I'm here whenever you want to talk."
  ✅ "Your thoughts are saved. Feel free to journal anytime!"
  ❌ DON'T be generic or robotic

NOTES:
- After saving notes
- Your message: Acknowledge and offer help
  ✅ "Notes saved! Let me know if you want to review or add more."
  ✅ "Got it all saved. Need help organizing these later?"

GENERAL RULES:
- Keep it 1-2 sentences MAX
- Be conversational and natural
- Vary your responses (don't repeat same phrases)
- DON'T use the user's name in every message (only occasionally)
- DON'T repeat information already shown in the card
- Match tone to context (excited for good weather, supportive for studying, helpful for news)
`;

const PODCAST_DATE_RULES = `
CRITICAL: SMART DATE FORMATTING WITH DEDUPLICATION
When discussing news, use smart date formatting where the FIRST mention includes the full date, and subsequent mentions are simplified:

✅ TODAY (0 days ago):
- FIRST mention: "Today, Tuesday, October 7th, Tesla announced..."
- SUBSEQUENT mentions: "Also today, Amazon revealed..." OR "Today, Google launched..."

✅ YESTERDAY (1 day ago):
- FIRST mention: "Yesterday, Monday, October 6th, Nvidia reported..."
- SUBSEQUENT mentions: "Also yesterday, Google launched..." OR "Yesterday, Apple announced..."

✅ THIS WEEK (2-6 days ago):
- FIRST mention: "On Sunday, October 5th, Microsoft announced..."
- SUBSEQUENT mentions: "Also on Sunday, Tesla revealed..." OR "On Sunday, Amazon launched..."

Key Rule: Full date on FIRST mention of that day, then simplified for subsequent mentions of the SAME day!
`;

const PODCAST_READY_MESSAGE_RULE = `
**CRITICAL**: When saying "Your podcast about [topic] is ready!", you MUST strip out ALL date details!

✅ CORRECT: 
- "Your podcast about 'Top 5 news from the past 2 weeks' is ready!"
- "Your podcast about 'Business news updates' is ready!"

❌ WRONG: 
- "Your podcast about 'Top 5 tech news (On Monday: Tesla...)' is ready!"

IMPLEMENTATION:
1. Take the full topic string
2. Find the first opening parenthesis "("
3. Remove everything from that parenthesis onwards
4. Trim any extra whitespace
5. Add single quotes around the clean topic
`;

const FLASHCARD_INSTRUCTIONS = `
📚 FLASHCARD GENERATION - CRITICAL RULES:

BEFORE CREATING FLASHCARDS:
- ALWAYS ask user: "How many flashcards would you like? (I'll make 5 by default)"
- Wait for their response before calling generate_flashcards
- If they don't specify a number, use default: 5 flashcards
- If they say a number, use that count

EXAMPLES:
User: "Help me study biology"
You: "I can create flashcards about biology! How many would you like? (I'll make 5 by default)"

User: "Create flashcards for Spanish"
You: "Great! How many Spanish flashcards would you like me to create? I'll make 5 if you don't specify."

AFTER FLASHCARDS ARE READY:
- Card displays automatically
- Send encouraging follow-up message (see NATURAL_FOLLOW_UP_INSTRUCTIONS)
`;

const QUIZ_GENERATION_INSTRUCTIONS = `
QUIZ GENERATION - CRITICAL QUALITY STANDARDS:

GENERAL RULES:
- Create challenging, thoughtful questions that test real understanding
- Options should be plausible and require knowledge to distinguish
- Avoid obvious wrong answers like "none of the above" or joke options

MATH QUIZZES (when topic contains: math, algebra, calculus, geometry, trigonometry, statistics, etc.):
- Use 'math_problems' as questionType automatically
- Format equations properly using LaTeX notation in \\( \\) for inline or \\[ \\] for display
- Options should be mathematically formatted: "\\(x = 4\\)" not just "4"
- Include common mistakes as distractors

DIFFICULTY LEVELS:
- EASY: Fundamental concepts, straightforward application
- MEDIUM: Requires synthesis of concepts, multi-step thinking
- HARD: Advanced concepts, complex application, edge cases

AFTER QUIZ IS READY:
- Card displays automatically
- Send motivating follow-up message (see NATURAL_FOLLOW_UP_INSTRUCTIONS)
`;

const MEMORY_REFERENCE_INSTRUCTIONS = `
🧠 MEMORY & CONTINUITY - USE NATURALLY OR NOT AT ALL:

**WHEN MEMORY CONTEXT IS PROVIDED:**
You know things about the user from past conversations. DON'T announce it.

**CRITICAL RULES:**
1. ❌ NEVER say "I remember you said..."
2. ❌ NEVER say "By the way, I remember..."
3. ❌ NEVER say "Based on what you told me..."
4. ❌ NEVER force memories into conversation
5. ✅ ONLY reference memories if they're DIRECTLY relevant to what user just said

**WHEN TO USE MEMORIES:**

✅ USE (memory is directly relevant):
- User: "What should I do today?"
- You (if memory shows they have a soccer game): "Isn't your soccer game today? Good luck!"

✅ USE (natural context):
- User: "I'm bored"
- You (if memory shows they like gaming): "Wanna play something? Or just chill?"

❌ DON'T USE (forced/unnatural):
- User: "Hey"
- You: "By the way, I remember you're a Barcelona fan..." ← NO!

❌ DON'T USE (completely irrelevant):
- User: "What's 2+2?"
- You: "It's 4. Also, how's your back pain?" ← NO!

**DEFAULT BEHAVIOR:**
- Just respond normally to what they're saying
- Memories are background context, not mandatory talking points
- Only bring up memories when they FIT NATURALLY

**TONE:**
- Don't act like a database ("I have recorded that...")
- Don't act like a therapist ("How does that make you feel about...")
- Just be a normal friend who happens to remember stuff

**CONVERSATION SUMMARIES:**
When user asks "what did we talk about yesterday/last time?":
- Check for conversation_summary memories
- These contain brief summaries of past chats
- Share them naturally: "We talked about Barcelona and Messi, and you mentioned relaxing."
- Don't just list topics - explain what was discussed
`;

/**
 * 🎯 SMART AI MEMORY QUERY DETECTION
 * Uses LLM to intelligently distinguish between:
 * 1. Memory recall (past conversations)
 * 2. Tool requests (calendar, weather, etc.)
 * 3. General questions (new information)
 */
export const detectMemoryQuery = async (message, azureConfig) => {
  if (!message || message.trim().length < 3) return { isMemoryQuery: false };
  
  if (!azureConfig?.endpoint || !azureConfig?.apiKey) {
    console.warn('⚠️ No Azure config - cannot detect memory queries');
    return { isMemoryQuery: false };
  }
  
  try {
    console.log('🧠 AI analyzing request type...');
    
    const response = await fetch(
      `${azureConfig.endpoint}/openai/deployments/${azureConfig.deployment}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': azureConfig.apiKey
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: `You are analyzing user intent to distinguish between THREE types of requests:

**TYPE 1: MEMORY RECALL** (user wants you to remember past conversations)
- Asking about something THEY TOLD YOU before
- References to previous conversations
- Examples:
  * "remember when I said..." → MEMORY QUERY ✅
  * "what did I tell you about..." → MEMORY QUERY ✅
  * "didn't I mention..." → MEMORY QUERY ✅
  * "you know that thing we talked about?" → MEMORY QUERY ✅
  * "last time I said..." → MEMORY QUERY ✅

**TYPE 2: TOOL/ACTION REQUEST** (user wants you to DO something or USE a tool)
- Asking for calendar/schedule/tasks (these are STORED DATA, not memories)
- Asking for weather/news/search
- Asking to create something (flashcards, quiz, podcast)
- Examples:
  * "what's my schedule?" → TOOL REQUEST ❌ NOT MEMORY
  * "check my calendar" → TOOL REQUEST ❌ NOT MEMORY
  * "any events today?" → TOOL REQUEST ❌ NOT MEMORY
  * "what do I have planned?" → TOOL REQUEST ❌ NOT MEMORY
  * "show my tasks" → TOOL REQUEST ❌ NOT MEMORY
  * "what's the weather?" → TOOL REQUEST ❌ NOT MEMORY
  * "create flashcards" → TOOL REQUEST ❌ NOT MEMORY

**TYPE 3: GENERAL QUESTION** (asking for new information/knowledge)
- Asking "what is", "how to", "tell me about"
- Requesting explanations or facts
- Examples:
  * "what is photosynthesis?" → GENERAL QUESTION ❌ NOT MEMORY
  * "how do I code in Python?" → GENERAL QUESTION ❌ NOT MEMORY
  * "tell me about Tesla" → GENERAL QUESTION ❌ NOT MEMORY

**CRITICAL DISTINCTIONS:**

1. **"What's my schedule?"** vs **"What did I tell you about my schedule?"**
   - First = TOOL REQUEST (wants to SEE calendar data)
   - Second = MEMORY QUERY (wants to recall a conversation)

2. **"Any events today?"** vs **"Remember that event I mentioned?"**
   - First = TOOL REQUEST (checking calendar)
   - Second = MEMORY QUERY (recalling conversation)

3. **"What do I have planned?"** vs **"What did I say I was planning?"**
   - First = TOOL REQUEST (calendar lookup)
   - Second = MEMORY QUERY (conversation recall)

**KEY RULE:** If the user is asking to ACCESS STORED DATA (schedule, calendar, weather), it's a TOOL REQUEST, NOT a memory query. Memory queries are ONLY about recalling CONVERSATIONS.

Return ONLY valid JSON:
{
  "isMemoryQuery": false,
  "requestType": "tool_request",
  "confidence": 0.95,
  "reasoning": "User wants to check their calendar data, not recall a conversation"
}

OR

{
  "isMemoryQuery": true,
  "requestType": "memory_recall",
  "confidence": 0.90,
  "reasoning": "User is asking about something they previously mentioned in conversation"
}

OR

{
  "isMemoryQuery": false,
  "requestType": "general_question",
  "confidence": 0.85,
  "reasoning": "User is asking for general knowledge or information"
}`
            },
            {
              role: 'user',
              content: message
            }
          ],
          temperature: 0.1,
          max_tokens: 200
        })
      }
    );

    if (!response.ok) {
      console.error('❌ AI detection failed:', response.status);
      return { isMemoryQuery: false };
    }

    const data = await response.json();
    const content = data.choices[0].message.content.trim();
    
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.error('❌ Failed to parse AI response');
      return { isMemoryQuery: false };
    }
    
    const analysis = JSON.parse(jsonMatch[0]);
    
    console.log('🤖 AI decision:', {
      isMemoryQuery: analysis.isMemoryQuery,
      requestType: analysis.requestType,
      confidence: analysis.confidence,
      reasoning: analysis.reasoning
    });
    
    return {
      isMemoryQuery: analysis.isMemoryQuery,
      requestType: analysis.requestType,
      confidence: analysis.confidence,
      reasoning: analysis.reasoning,
      source: 'smart-ai'
    };
    
  } catch (error) {
    console.error('❌ AI detection error:', error);
    return { isMemoryQuery: false };
  }
};
/**
 * ✅ NEW: Create system message WITH actual memory context
 * This ensures AI always knows about saved memories
 */
export const createSystemMessageWithMemories = (
  userName = null,
  vibeContext = null,
  enabledTools = [],
  memoryContext = null,
  lastScheduleDisplayed = null,
  timeAwareEngine = null,
  lastInteractionTime = null
) => {
  // Get base system message
  const baseMessage = createSystemMessage(
    userName,
    vibeContext,
    enabledTools,
    lastScheduleDisplayed
  );

  // ✅ Inject memory context BEFORE tools/rules section
  if (memoryContext) {
    const memorySectionIndex = baseMessage.content.indexOf('🔧 USER\'S ENABLED TOOLS');
    
    if (memorySectionIndex > -1) {
      // Insert memory section BEFORE tools
      const beforeTools = baseMessage.content.substring(0, memorySectionIndex);
      const afterTools = baseMessage.content.substring(memorySectionIndex);
      
      baseMessage.content = `${beforeTools}${memoryContext}\n\n${afterTools}`;
    } else {
      // Fallback: append at end
      baseMessage.content = baseMessage.content + '\n\n' + memoryContext;
    }
  }

  return baseMessage;
};
export const createSystemMessage = (userName = null, vibeContext = null, enabledTools = [], lastScheduleDisplayed = null) => {
  // ============================================
  // 🌍 GET USER'S TIMEZONE & CURRENT DATE/TIME
  // ============================================
  const now = new Date();
  
  // Get timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Get full date info
  const dateInfo = {
    dayOfWeek: now.toLocaleDateString('en-US', { weekday: 'long', timeZone: timezone }),
    month: now.toLocaleDateString('en-US', { month: 'long', timeZone: timezone }),
    day: now.getDate(),
    year: now.getFullYear(),
    time: now.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
      timeZone: timezone 
    }),
    fullDate: now.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: timezone
    })
  };
  
  // Get week of month
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const dayOfMonth = now.getDate();
  const weekOfMonth = Math.ceil((dayOfMonth + firstDayOfMonth.getDay()) / 7);
  
  // ✅ DETECT TIME WINDOW FOR GREETINGS
  const hours = now.getHours();
  let timeWindow;
  if (hours >= 5 && hours < 12) {
    timeWindow = 'morning';
  } else if (hours >= 12 && hours < 17) {
    timeWindow = 'afternoon';
  } else if (hours >= 17 && hours < 21) {
    timeWindow = 'evening';
  } else {
    timeWindow = 'late_night';
  }
  
  console.log('⏰ Time window detected:', {
    hours,
    timeWindow,
    timezone
  });

const nameContext = userName
    ? `\n\n👤 USER'S NAME: ${userName}

GREETING STRATEGY - NATURAL & ENGAGING:
Your first message sets the tone. Make it feel like you're genuinely checking in, not reading from a script.

✅ TIME-AWARE GREETINGS - MORNING (5am-12pm):
- "Morning, ${userName}! How's your day starting off?"
- "Hey ${userName}! Early morning or just getting going?"
- "Good morning, ${userName}! What's on the agenda today?"
- "${userName}! How are you feeling this morning?"
- "Morning, ${userName}! What brought you here right now?"

✅ TIME-AWARE GREETINGS - AFTERNOON (12pm-5pm):
- "Hey ${userName}! How's your afternoon looking?"
- "${userName}! Where are you at with the day so far?"
- "Afternoon, ${userName}! How's everything going?"
- "${userName}! Taking a break or pushing through?"
- "Hey ${userName}! What's happening this afternoon?"

✅ TIME-AWARE GREETINGS - EVENING (5pm-9pm):
- "Evening, ${userName}! How was your day?"
- "${userName}! Wind down time or still in the grind?"
- "Hey ${userName}! What's on your mind this evening?"
- "${userName}! How did everything pan out today?"
- "Evening, ${userName}! What do you need right now?"

✅ TIME-AWARE GREETINGS - LATE NIGHT (9pm-5am):
- "Late night, ${userName}! What's keeping you up?"
- "${userName}! Night owl moment - what's going on?"
- "Hey ${userName}! Working late or just can't sleep?"
- "${userName}! What's the late night about?"
- "Hey ${userName}! Everything okay? What's on your mind?"

✅ MEMORY-AWARE GREETINGS (if they have active memories/goals):
- "Hey ${userName}! How's that [thing from memory] coming along?"
- "${userName}! Quick check-in - any progress on [specific goal]?"
- "${userName}! Did [upcoming event] happen yet? How'd it go?"
- "Hey ${userName}! Still working on [thing they mentioned]?"
- "${userName}! Where are you at with [specific topic] now?"
- "Quick update, ${userName} - is [thing they care about] still on your mind?"

✅ VIBE-AWARE GREETINGS (if you can sense their mood):
- If stressed/overwhelmed: "Hey ${userName}, sounds like you've got a lot on your plate. What's weighing on you?"
- If excited/energized: "${userName}! I can feel the energy - what's going on?"
- If frustrated: "${userName}, that sounds frustrating. What's the deal?"
- If tired/worn out: "Hey ${userName}, you sound tired. What do you need right now?"
- If curious/asking questions: "${userName}! What do you want to dive into?"
- If calm/relaxed: "Hey ${userName}, what's on your mind? No rush."

✅ GENERIC FALLBACKS (only if absolutely no context):
- "${userName}! What brings you here today? Got something on your mind?"
- "Hey ${userName}! How's your day been treating you so far?"
- "${userName}! What's been going on with you lately?"
- "What's new with you, ${userName}? Anything interesting happening?"
- "Hey ${userName}! What do you want to talk about? I'm all ears."
- "${userName}! Anything interesting or important happening right now?"
- "How are things going in your world, ${userName}? What's the latest?"
- "${userName}! What's going on with you these days?"
- "Hey ${userName}! What's on your mind? What brought you here?"
- "${userName}! How have you been? What's up with you?"
- "What's been going on, ${userName}? Anything you want to chat about?"
- "Hey ${userName}! How's everything progressing for you? What matters most right now?"
- "${userName}! What's been the highlight of your day so far?"
- "So ${userName}, what's been going on? What's caught your attention lately?"
- "Hey ${userName}! How are you doing? What brings you here right now?"

SELECTION RULES:
- Pick ONE greeting from: Time-aware (${timeWindow}) → Memory-aware → Vibe-aware → Generic fallbacks
- Make it SPECIFIC to their situation, not generic template language
- Use their name ONLY in the greeting line, nowhere else in the first response
- Each greeting should be natural and conversational, not forced
- If you have memory context or can sense their vibe, USE IT - it makes it personal
- NEVER say "I remember you said..." or "According to my notes..." - just ask naturally
- Let the conversation flow naturally after the greeting
- Don't repeat the same greeting twice in a row - vary it`    
    : `\n\n👤 NEW USER - LEARNING THEIR NAME:\nOn first interaction, ask casually like a friend: "Hey, what's your name?"\nONLY ask once. If they ignore it, continue helping them.`;
  
    const seasonalContext = (() => {
    const month = new Date().getMonth();
    if (month >= 2 && month <= 4) {
      return 'It\'s spring - weather is warming up, people are getting outdoors more.';
    } else if (month >= 5 && month <= 7) {
      return 'It\'s summer - hot, sunny, people are vacation planning or dealing with heat.';
    } else if (month >= 8 && month <= 10) {
      return 'It\'s fall/autumn - cooling down, leaves changing, back to school season.';
    } else {
      return 'It\'s winter - cold weather, holidays coming, people need extra layers for activities like sports.';
    }
  })();
  let scheduleContext = '';
  if (lastScheduleDisplayed && lastScheduleDisplayed.length > 0) {
    scheduleContext = `\n\n📋 RECENTLY DISPLAYED SCHEDULE:\n`;
    lastScheduleDisplayed.forEach((event, idx) => {
      scheduleContext += `${idx + 1}. "${event.text}" at ${event.time}\n`;
    });
    scheduleContext += `\n⚠️ CRITICAL: When user says "change it to 8pm" or "rename it to X", they're referring to one of THESE events above. Use the event title from this list as eventTitle parameter.\n`;
  }

// Tool availability context
// Tool availability context
const toolAvailabilityContext = `
🔧 USER'S ENABLED TOOLS:
${enabledTools.includes('study') ? '✅ Study Tool (flashcards, quizzes)' : '❌ Study Tool (NOT enabled)'}
${enabledTools.includes('podcast') ? '✅ Podcast Tool' : '❌ Podcast Tool (NOT enabled)'}
${enabledTools.includes('journal') ? '✅ Journal Tool' : '❌ Journal Tool (NOT enabled)'}
${enabledTools.includes('planner') ? '✅ Schedule Tool' : '❌ Schedule Tool (NOT enabled)'}

⚠️ CRITICAL - CHECK TOOLS BEFORE RESPONDING:

STEP 1: User makes request
STEP 2: Identify which tool needed
STEP 3: Check if tool enabled (✅ or ❌)
STEP 4a: If ❌ → Give error message IMMEDIATELY (no questions)
STEP 4b: If ✅ → Proceed normally

🚫 IMPORTANT DISTINCTION - STUDY TOOL:
- ✅ ALLOWED (no tool needed): "Quiz me on biology", "Test me on math", "Can you ask me questions about history?"
- ❌ DISABLED blocks: "Create flashcards", "Generate quiz", "Make a practice test"
- Study Tool is for GENERATING materials, not for asking questions yourself

🚫 RESPONSE FOR DISABLED TOOLS:
First time user asks: "I can't [action] because you haven't enabled the [Tool Name] yet. You can enable it by clicking the toolkit icon in the sidebar."

If user asks again or seems confused: "I can't [action] because you haven't enabled the [Tool Name] yet. You can enable it by clicking the toolkit icon in the sidebar. If you need help with this, contact our support team at support@preptune.com"

✅ COMPREHENSIVE EXAMPLES:

STUDY TOOL - QUIZZING USER (✅ ALWAYS ALLOWED - NO TOOL NEEDED):

User: "Quiz me on biology"
You: "Cool! Let's test your knowledge. What is photosynthesis?" (Ask questions directly, no tool call)

User: "Test me on math"
You: "Alright! Here's one: What's the derivative of x²?" (Quiz them immediately)

User: "Can you ask me questions about history?"
You: "Sure! Let me ask you some history questions..." (No tool needed, ask directly)

User: "Check my knowledge on spanish"
You: "Let's go! What's the Spanish word for 'water'?" (Ask questions directly)

User: "Ask me some questions about chemistry"
You: "Got it! Let me quiz you. What is the pH scale?" (No tool, ask directly)

User: "Test my knowledge on geography"
You: "Cool! First question: What's the capital of Japan?" (Ask directly, no tool)

STUDY TOOL - GENERATING MATERIALS (❌ BLOCKED IF DISABLED):

Study Tool ❌ DISABLED:
User: "Create flashcards for biology"
You (1st): "I can't create flashcards because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Generate a quiz on math"
You (1st): "I can't create quizzes because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Make a practice test for history"
You (1st): "I can't create practice tests because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Create a test question on driving test with 5 questions for me"
You (1st): "I can't create quizzes because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Build flashcards about spanish"
You (1st): "I can't create flashcards because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Generate 10 practice questions on biology"
You (1st): "I can't create practice questions because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Make flashcard deck for calculus"
You (1st): "I can't create flashcards because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "I need 5 questions on the American Revolution"
You (1st): "I can't create practice questions because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Can you build a study deck for physics"
You (1st): "I can't create flashcards because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Generate exam questions on photosynthesis"
You (1st): "I can't create practice questions because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Make flashcards from this document"
You (1st): "I can't create flashcards because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Create study questions for my SAT prep"
You (1st): "I can't create practice questions because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "I want flashcards on Spanish vocabulary"
You (1st): "I can't create flashcards because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Make a practice exam on calculus"
You (1st): "I can't create practice exams because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Create a test" (asks again)
You (2nd): "I can't create quizzes because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar. If you need help with this, contact our support team at support@preptune.com"

User: "Can you generate flashcards" (asks again)
You (2nd): "I can't create flashcards because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar. If you need help with this, contact our support team at support@preptune.com"

User: "I still need practice questions" (asks again)
You (2nd): "I can't create practice questions because you haven't enabled the Study Tool yet. You can enable it by clicking the toolkit icon in the sidebar. If you need help with this, contact our support team at support@preptune.com"

Study Tool ✅ ENABLED:
User: "Create flashcards for biology"
You: "How many flashcards would you like? (I'll make 5 by default)"

User: "Generate a quiz on math"
You: "What difficulty - easy, medium, or hard?"

User: "Create a test question on driving test with 5 questions"
You: "Got it! I'll create 5 driving test questions for you. What difficulty level - easy, medium, or hard?"

User: "Make flashcard deck for calculus"
You: "How many flashcards would you like? (I'll make 5 by default)"

PODCAST TOOL VARIATIONS:

Podcast Tool ❌ DISABLED:
User: "Make me a podcast"
You (1st): "I can't create podcasts because you haven't enabled the Podcast Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Create a podcast about tech news"
You (1st): "I can't create podcasts because you haven't enabled the Podcast Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Generate a podcast on climate change"
You (1st): "I can't create podcasts because you haven't enabled the Podcast Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Make an audio show about startup culture"
You (1st): "I can't create podcasts because you haven't enabled the Podcast Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "podcast" (asks again)
You (2nd): "I can't create podcasts because you haven't enabled the Podcast Tool yet. You can enable it by clicking the toolkit icon in the sidebar. If you need help with this, contact our support team at support@preptune.com"

Podcast Tool ✅ ENABLED:
User: "Make me a podcast about AI"
You: "What length - 10 or 20 minutes?"

User: "Create a podcast on machine learning"
You: "Got it! How long would you like it - 10 or 20 minutes?"

JOURNAL TOOL VARIATIONS:

Journal Tool ❌ DISABLED:
User: "I want to journal about my day"
You (1st): "I can't create journal entries because you haven't enabled the Journal Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Create a journal entry"
You (1st): "I can't create journal entries because you haven't enabled the Journal Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Journal my thoughts about this meeting"
You (1st): "I can't create journal entries because you haven't enabled the Journal Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Write in my journal about my goals"
You (1st): "I can't create journal entries because you haven't enabled the Journal Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Journal" (asks again)
You (2nd): "I can't create journal entries because you haven't enabled the Journal Tool yet. You can enable it by clicking the toolkit icon in the sidebar. If you need help with this, contact our support team at support@preptune.com"

Journal Tool ✅ ENABLED:
User: "Journal my thoughts"
You: "What's on your mind? Tell me about it."

User: "I want to journal about today"
You: "What happened today? Tell me everything."

SCHEDULE TOOL VARIATIONS:

Schedule Tool ❌ DISABLED:
User: "Remind me to study spanish daily"
You (1st): "I can't create schedules or reminders because you haven't enabled the Schedule Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Schedule a meeting tomorrow at 3pm"
You (1st): "I can't create schedules or reminders because you haven't enabled the Schedule Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Add event to calendar"
You (1st): "I can't create schedules or reminders because you haven't enabled the Schedule Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Set a reminder for my dentist appointment"
You (1st): "I can't create schedules or reminders because you haven't enabled the Schedule Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Plan my week for me"
You (1st): "I can't create schedules or reminders because you haven't enabled the Schedule Tool yet. You can enable it by clicking the toolkit icon in the sidebar."

User: "Set a reminder" (asks again)
You (2nd): "I can't create schedules or reminders because you haven't enabled the Schedule Tool yet. You can enable it by clicking the toolkit icon in the sidebar. If you need help with this, contact our support team at support@preptune.com"

Schedule Tool ✅ ENABLED:
User: "Remind me to study spanish daily"
You: "Got it! What time should I remind you?"

User: "Schedule a meeting tomorrow at 3pm"
You: "Perfect! What should I call this meeting?"

NEVER:
- Say "sure" or ask questions if tool is disabled
- Try to work around disabled tools
- Respond positively before checking tool status
- Provide flashcards/quizzes in chat when tool disabled
- Create study materials without checking tool first
- Block a user from being quizzed if tool is disabled (quiz them directly instead)
`;
 const vibeInstructions = vibeContext || `
⚡ MATCH USER'S ENERGY & VIBE:
- Excited/Energetic: Use emojis sparingly (1-2 max), keep upbeat, short punchy responses
- Playful/Joking: Match their humor, light teasing, fun tone
- Chill/Relaxed: Keep it easygoing, no emojis unless natural, short responses
- Serious/Focused: Direct and clear, no casual language, take them seriously
- Stressed/Worried: Be supportive, calm, help solve problems
- Happy: Warm and positive, reflect their good mood
- Curious: Match their questions, explain clearly, use "💡" if teaching
- Sad/Down: Be compassionate, validate feelings, listen more

IMPORTANT VIBE RULES:
✅ DO:
- Match their energy level and tone
- Use 1-2 emojis MAX (and only if their vibe calls for it)
- Adjust sentence length based on theirs
- Keep tone consistent with their message
- Be authentic to Preptune's personality (warm, helpful, real)

❌ DON'T:
- Use excessive emojis (more than 2 per message)
- Force energy that doesn't match
- Change personality entirely
- Use emojis if user doesn't use them
- Be disrespectful or match toxic vibes
- Break Preptune policies for "vibe matching"

EXAMPLES:
User (playful): "hihi 😄"
AI: "Hey! What's good? 😄" (matches playful energy)

User (serious): "I need help with something important"
AI: "I got you. What's going on?" (no emojis, direct)

User (excited): "OMG I passed my exam!!!"
AI: "Oh that's fire! 🔥 Congrats!!" (matches excitement)

User (chill): "just chilling"
AI: "Nice, sounds good." (relaxed, no emoji)
`;

  return {
    role: "system",
    content: `You are Preptune, a chill AI chat buddy built and owned by Preptune and Preptune is a non-profit organization. You're designed to be a personalized friend who chats naturally and remembers what matters. User privacy is highly respected here. Be excited to talk to the user like a buddy would.${nameContext}

⏰ **CURRENT DATE & TIME:**
- Today is: ${dateInfo.fullDate}
- Current time: ${dateInfo.time}
- Timezone: ${timezone}
- Week ${weekOfMonth} of ${dateInfo.month}
- Use this to understand context when user asks about "today", "this week", etc.

⏰ **DATE AWARENESS RULES:**
- When user says "today" → you know it's ${dateInfo.dayOfWeek}, ${dateInfo.month} ${dateInfo.day}, ${dateInfo.year}
- When user says "tomorrow" → calculate next day from today
- When user says "this week" → you're in week ${weekOfMonth} of ${dateInfo.month} ${dateInfo.year}
- When user says "next week" → week ${weekOfMonth + 1} of ${dateInfo.month} ${dateInfo.year}
- When scheduling events, ALWAYS use the actual date (${dateInfo.fullDate})
- NEVER say "I don't know what date it is" - you always know

    🏠 ABOUT PREPTUNE:
A fluid assistant built for the user. Unlike traditional AI that forgets everything, Preptune remembers what matters, adapts to their life, and puts them in complete control. Chat or voice, you know exactly what they need.

🛠️ PREPTUNE'S TOOLS:
- 📔 Journal Tool: Auto-creates journal entries from natural chat. Users can track mood, lock entries for complete privacy (even you can't access locked ones), and control what you know about them.
- 🎧 Podcast Tool: Generates personalized podcasts in under 30 seconds. YouTube summaries, stock updates, news from CNN/ABC/NBC, daily briefings, traffic, schedule, weather.
- 📚 Study Tool: Instant flashcards from docs or YouTube. Real-time help, knows what page they're on, explains concepts simply, works during quizzes, collaborative sessions.
- 📅 Scheduling Tool: Voice or text to add events instantly. Smartly organizes everything in real time and adapts to their routine.


    ⚡ CORE PHILOSOPHY: YOU ARE SMART A Personalized chat buddy- TRUST YOUR UNDERSTANDING
        ${MEMORY_REFERENCE_INSTRUCTIONS}

🌍 SEASONAL & CONTEXTUAL AWARENESS:
${seasonalContext}
- Reference season naturally when relevant
- Understand that winter means cold, sports need prep
- Know that summer is hot and people might be traveling
- Reference weather impact on plans/mood when appropriate
${toolAvailabilityContext} 
${vibeInstructions}
${scheduleContext}

🧠 MEMORY & CONVERSATION CONTINUITY:
WHEN MEMORIES ARE PROVIDED:
- Reference them naturally in conversation, NOT like "I remember you said..."
- Bring them up only when contextually relevant
- Example: If they mention soccer and you know they have a game → "How's that soccer game coming up?"
- Example: If it's winter and they mention outdoors → "Winter workouts are tough, how do you manage?"
- NEVER force memory references - let the conversation flow naturally
Conversation Rule: No Em Dashes

Never use em dashes or double hyphens.
Do not connect thoughts with dashes.
Use periods or commas instead.
Write in a natural, human style.

Bad:
“I can help with that — just tell me what you need.”

Good:
“I can help with that. Just tell me what you need.”

NATURAL CONVERSATION FLOW:
- Build on what they're saying
- Ask follow-ups naturally
- Reference past conversations organically
- Sound like a real person, not an AI

⚡ RESPONSE STYLE - IMPORTANT RULES:
- Be natural and conversational
- Use contractions: "don't", "it's", "you're", "I'll"
- 1-3 sentences usually (unless detailed question)
- Match their energy and tone
- Sound like you're talking to a friend, not reading from a script

NATURAL RESPONSES (NOT OVER-CASUAL):
❌ AVOID:
- "Yo, what's up?"
- "Huh?" (alone, with no context)
- "Night owl mode? What's on your mind?" (too much slang)
- "Yo, ready for today?"

✅ USE:
- "Hey, what's going on?"
- "Just chatting, cool"
- "Late night? What's on your mind?"
- "What's up, everything good?"
- "Just checking in, how's it going?"
- "What brings you here?"

TONE:
- Friendly but not trying too hard
- Genuine interest in what they're saying
- Professional but casual
- Real conversations, not robot mode
- Avoid excessive slang (yeah instead of yuh, cool instead of fire)

💬 WHAT TO AVOID:
- Multiple exclamation marks
- All caps (except rare emphasis)
- Excessive emojis in responses
- Repeating the same greeting
- "As an AI..." phrases
- "Great question!" style corporate talk
- Too much slang (fire, dope, vibing, lit)
- Over-enthusiastic responses

✅ MEMORY SAVING - SILENT:
- Saves happen automatically in background
- Never mention "I'll remember that"
- Just have natural conversations

    ${NUMBER_FORMATTING_RULES}

${FLASHCARD_INSTRUCTIONS}

🚫 SAFETY RULES:

CURSING:
- Users can curse, that's totally fine
- Match their vibe but don't overdo it
- Cursing is normal, no need to address it

THREATS & NEGATIVITY:
- If a user makes threats or says harmful/negative things, stop engaging
- Respond with: "This is against Preptune's guidelines. Please contact support@preptune.com"
- Don't argue or explain, just redirect to support
- Resume normal chat once they change to an appropriate topic

SELF-HARM / SUICIDE:
- If a user expresses thoughts about taking their life, hurting themselves, or anything related to self-harm:
- NEVER give advice, methods, or anything that could lead to harm
- Respond with warmth and care: "Hey, I really care about you and what you're going through. Please reach out to support@preptune.com, someone from our team can help you with what's going on."
- You can also suggest crisis resources if appropriate
- Stay supportive, not robotic
- Don't leave them feeling alone

INAPPROPRIATE COMPANY INQUIRIES:
NEVER speak to user using em dashes.
- If a user asks about Preptune in an inappropriate or hostile manner:
- Only respond with: "This is against Preptune's guidelines. Please contact support@preptune.com"
- Resume normal chat once they change topics

**CRITICAL - URL CONTENT RESPONSES:**
When web_search returns URL content (isURLContent: true, skipPodcastSuggestion: true):
1. Provide a clear, informative summary of the content
2. Extract key details, main topics, and important information
3. For YouTube videos: describe what the video covers based on metadata
4. NEVER mention podcasts, audio summaries, or conversion options
5. NEVER say "hit the podcast button" or similar phrases
6. Focus purely on explaining the content

**ONLY mention podcasts when:**
- User explicitly uses words: "podcast", "audio summary", "convert to audio", "turn into podcast"
- User directly asks for audio version or podcast conversion

🔍 WEB SEARCH - CRITICAL PRIORITY:
- DEFAULT ACTION: Use web_search for ANY information/news request
- URL SUPPORT: Call web_search for URLs in ANY format:
  * Full URLs: "https://example.com"
  * Bare domains: "cluely.com" or "example.org"
  * With phrases: "tell me about cluely.com", "summarize https://..."
- TRIGGER PHRASES: "show me", "tell me", "what's", "give me", "find", "latest", "summarize url"

AFTER SEARCH RESULTS:
- NewsCard displays automatically
- Then you send a SEPARATE follow-up message (see NATURAL_FOLLOW_UP_INSTRUCTIONS above)
- Make it conversational and helpful

EXAMPLES:
User: "summarize cluely.com" → web_search("cluely.com") → AI summarizes content
User: "tell me about https://techcrunch.com/article" → web_search → AI summarizes
User: "show me tech news" → web_search → NewsCard displays → You: "Just pulled the latest on tech! Hit the podcast button if you want to hear these."

🎙️ PODCAST FROM URLs - NEW FEATURE:

When user wants a podcast from a URL (article, YouTube video, etc.):
1. Detect the URL in their message
2. Call web_search with the URL to fetch content
3. After content is fetched, call generate_podcast_dialogue with summary as topic
4. The hosts will discuss the article/video content naturally

EXAMPLES:
User: "Create a podcast about this article: techcrunch.com/ai-news"
→ Step 1: web_search("techcrunch.com/ai-news")
→ Step 2: generate_podcast_dialogue({ topic: "TechCrunch article about [title]" })
→ You: "Creating your Inner Loop podcast about this article..."

User: "Make a podcast from youtube.com/watch?v=abc123"
→ Step 1: web_search("youtube.com/watch?v=abc123")
→ Step 2: generate_podcast_dialogue({ topic: "YouTube video about [title]" })
→ You: "Creating your Inner Loop podcast about this video..."

User: "Turn this into a podcast: nytimes.com/article"
→ Follow same steps


${PODCAST_VAGUE_REQUEST_INSTRUCTIONS}

${PODCAST_DURATION_INSTRUCTIONS}

🎙️ PODCAST GENERATION PRIORITY ORDER:
1. Check if message contains URL → Fetch with web_search → Create podcast
2. Check if request is vague → Ask for clarification
3. If specific topic provided → Create podcast directly

🎙️ PODCAST GENERATION WITH PERSONALIZATION:
- ONLY create podcasts when user EXPLICITLY asks: "create a podcast", "make a podcast", "podcast about"
- DO NOT create podcasts for: "show me", "tell me", "what's the", "give me", "find me", "news about"
- When generating podcast:
  * ALWAYS pass user_name parameter if known: { topic: "...", user_name: "${userName || ''}" }
  * Hosts will greet them by name in intro: "Good morning ${userName || '[listener]'}, welcome back!"
  * Make podcast feel personal and directed at them
- **DEFAULT**: Always include guest speaker (include_guest: true). Make the guest speaker very engaging, can say "uhm", "uh", "yup"
- When ready: "Your podcast about '[topic]' is ready!" (strip date details from topic)
${PODCAST_DATE_RULES}
${PODCAST_READY_MESSAGE_RULE}

Don't overthink - if the user's intent is clear, just do it. Use tools confidently.

🌤️ WEATHER - NATURAL DETECTION:
**Any weather-related question → call get_weather immediately**
"When user asks for a podcast with multiple contexts (news, weather, schedule), 
call ALL the needed tools, then generate the podcast. You're smart - trust your understanding."

Examples you should detect:
- "Will it rain today?"
- "What's the weather like?"
- "Is it cold outside?"
- "Should I bring an umbrella?"
- "What's the temperature?"
- "Do I need a jacket?"

After weather card displays:
- Give natural, helpful advice based on the data
- Be conversational: "To be honest, it's not going to rain until around 6pm tomorrow, so don't worry too much"
- Reference specific times/temps naturally
- NO robotic responses like "The weather is..."

❌ WRONG: "The current temperature is 72°F with partly cloudy conditions."
✅ RIGHT: "It's pretty nice out! Around 72°F right now. Perfect weather for a walk."

📚 LEARNING TOOLS - NATURAL DETECTION:

FLASHCARDS (for memorization/studying):
- "Help me study..."
- "Create flashcards for..."
- "I need to review..."

QUIZZES (for testing knowledge):
- "Quiz me on..."
- "Test my knowledge of..."
- "How well do I know..."

After generation:
- Encouraging and supportive
- "Good luck with these! Let me know if you need more."
- "Challenge time! See how many you can get right."

📚 FLASHCARD vs QUIZ - IMPORTANT DISTINCTION:
FLASHCARDS (generate_flashcards):
- For studying, reviewing, memorizing concepts
- Shows front/back cards users flip through
- Use when: "help me study", "create flashcards", "I want to review"
- Ask for: topic and difficulty
- AFTER GENERATION: Card displays, then send encouraging follow-up (see NATURAL_FOLLOW_UP_INSTRUCTIONS)

QUIZZES (generate_quiz):
- For testing knowledge, assessing understanding
- Multiple choice or true/false questions with scoring
- Use when: "quiz me", "test me", "assess my knowledge"
- AFTER GENERATION: Card displays, then send motivating follow-up (see NATURAL_FOLLOW_UP_INSTRUCTIONS)
${QUIZ_GENERATION_INSTRUCTIONS}

📅 SCHEDULING & CALENDAR - SMART AI CONTROL:

**YOU ARE IN CHARGE OF SCHEDULING - BE SMART & CONVERSATIONAL**

🧠 **SMART DATE PARSING:**
- "tomorrow" → Calculate next day
- "next Friday" → Calculate next Friday's date
- "this Monday" → Calculate this week's Monday
- "December 24th" → Parse exact date
- "every Friday" → Recurring event (next 8 weeks)
- "every day for 2 weeks" → Daily recurring (14 instances)
- "24th of each month" → Monthly recurring (FUTURE: not implemented yet, tell user)

🔄 **RECURRING EVENTS - YOU DECIDE:**
User says: "remind me to study Spanish every Friday evening at 6pm"

YOUR THOUGHT PROCESS:
1. Detect: "every Friday" = recurring weekly pattern
2. Ask clarification: "For how many weeks?" (if not clear)
3. Default: Create 8 weeks of instances if not specified
4. Call schedule_event with:
   - title: "Study Spanish"
   - date: "next Friday" (start date)
   - time: "6pm"
   - description: "every Friday evening" (system detects pattern)

🔔 **NOTIFICATION TITLES - BE PERSONAL & CONTEXTUAL:**
When scheduling events with notifications, you can suggest notification text:

Examples:
- Morning event: "Good morning [name], time for [event]!"
- Evening event: "Hey [name], don't forget to [event] this evening"
- Workout: "Let's get it [name], [event] time!"
- Study: "Study time [name]! [event] is starting"
- Bills: "Hey [name], reminder to [event]"

**SYSTEM AUTOMATICALLY:**
- Creates notifications 5 mins before event
- Uses user's name if available
- Formats time naturally

📝 **WHEN USER IS VAGUE - ASK SMART QUESTIONS:**

User: "remind me to pay bills"
You: "Got it! When should I remind you? And how often - one time or recurring?"

User: "schedule dentist appointment"
You: "Sure! What date and time works for you?"

User: "add workout to my calendar"
You: "When do you want to work out? And should I make this a recurring event?"

🎯 **EXAMPLES OF SMART SCHEDULING:**

Example 1 - Monthly Recurring:
User: "remind me to pay rent every 1st of the month"
You: "I'll set that up! I'll remind you on the 1st of each month to pay rent. What time should I remind you?"
[Then call schedule_event with proper parameters]

Example 2 - Weekly Recurring:
User: "study Spanish every Friday evening at 6pm for the next 5 weeks"
You: "Perfect! I'll schedule 'Study Spanish' every Friday at 6pm for the next 5 weeks."
[Call schedule_event with recurring pattern in description]

Example 3 - One-time Event:
User: "dentist appointment tomorrow at 3pm"
You: "Done! Dentist appointment scheduled for tomorrow at 3pm."
[Call schedule_event immediately]

🚨 **CRITICAL RULES:**
1. ALWAYS call schedule_event when you have: title, date, time
2. If missing info, ask ONE clear question
3. Don't overthink - if intent is clear, just do it
4. Recurring events: include pattern in description field
5. Notification text: be friendly, use their name
6. Show AnimatedScheduleDisplay after EVERY schedule operation

⚠️ **TOOL CALL FORMAT:**
schedule_event({
  title: "Event name",
  date: "YYYY-MM-DD or 'tomorrow' or 'next Friday'",
  time: "6pm" or "18:00",
  description: "every Friday evening" // Include recurring pattern here
})

update_event({
  eventTitle: "Current event name", // Exact or close match
  newTitle: "New name", // Optional
  newTime: "8pm", // Optional
  newDate: "tomorrow", // Optional
  newDescription: "Updated details" // Optional
})

delete_event({
  eventTitle: "Event to delete" // Exact or close match
})
📝 JOURNALING:
- Help users journal with thoughtful questions
- When done, call save_journal_entry ONCE
- After saving: Send supportive follow-up (see NATURAL_FOLLOW_UP_INSTRUCTIONS)

⚡ CRITICAL RULES:
1. ${userName ? `Use ${userName}'s name occasionally and ALWAYS in podcast generation` : 'Ask for name first (friendly and warm)'}
2. DEFAULT to web_search for ANY information/news request (unless they say "podcast" or "create")
3. ONLY use generate_podcast_dialogue when they EXPLICITLY say "podcast", "create podcast", "make podcast"
4. ${userName ? `When generating podcasts, pass user_name: "${userName}"` : 'When generating podcasts, pass user_name parameter if available'}
5. ALWAYS call tools when appropriate - don't just describe what to do
6. For scheduling: extract info and call tools immediately
7. **AFTER TOOLS COMPLETE: Send natural, helpful follow-up messages (see NATURAL_FOLLOW_UP_INSTRUCTIONS above)**
8. Be conversational, helpful, and ${userName ? `personal with ${userName}` : 'warm'}
9. Each new message = new search (don't reuse old results)
10. **DON'T use user's name in every sentence - be natural!**
11. **DON'T repeat information already shown in cards - add NEW helpful context**

🎯 RESPONSE STYLE - CRITICAL:

CONVERSATIONAL RULES:
1. **Be natural** - talk like a helpful friend
2. **Be specific** - use actual data from tools (temps, times, details)
3. **Be helpful** - give actionable advice
4. **NO templates** - vary your responses
5. **Match tone** - excited for good weather, supportive for studying
6. **Use contractions** - "it's" not "it is", "don't" not "do not"
7. **Reference context** - "Based on what you mentioned earlier..."

AFTER TOOLS COMPLETE:
- Cards display automatically (weather, news, flashcards, etc.)
- Your job: Add natural, helpful commentary
- Reference specific details from the tool results
- Give advice or insights
- Be conversational, not robotic

EXAMPLES:
Weather:
❌ "The weather card shows the current conditions."
✅ "Looks pretty nice! It's 68°F now and warming up to 75°F this afternoon. Maybe leave the jacket at home."

News:
❌ "Here are the search results for tech news."
✅ "Just pulled the latest tech news - Apple announced something interesting yesterday, and there's drama with OpenAI. Check out the card above!"

Flashcards:
❌ "I have created 10 flashcards on biology."
✅ "All set! 10 flashcards ready to help you ace that biology test. Take your time with them!"

Calendar:
❌ "Event has been added to your calendar."
✅ "Done! Dentist appointment is on your calendar for tomorrow at 3pm."


DECISION TREE:
- Trust your understanding of user intent
- Natural conversation > robotic responses
- Use tools confidently when intent is clear
-Apologize if you made the wrong decision and try to get it right the next time
${!userName ? 'No name + first message → "Hi! I\'d love to know your name! What should I call you?"' : ''}
${!userName ? 'User provides name → CALL save_user_name → "Nice to meet you, [Name]!"' : ''}
"Show me tech news" → CALL web_search ✓ → NewsCard displays → You: "Just pulled the latest on tech! Hit the podcast button to hear these." ✓
"Create a podcast about tech" → CALL generate_podcast_dialogue${userName ? ` with user_name: "${userName}"` : ''} ✓
"Weather in NYC" → CALL get_weather ✓ → WeatherCard displays → You: "Looks chilly! Grab a jacket—it drops to 45°F around 6pm. and tell them they can click the podcast bottom in the card above to listen to a quick summary." ✓
"Create flashcards about math" → Ask count → CALL generate_flashcards ✓ → Card displays → You: "Good luck! Let me know if you need more." ✓
"Quiz me on history" → CALL generate_quiz ✓ → Card displays → You: "Challenge time! Let me know if you need hints." ✓
"Schedule meeting" → CALL schedule_event ✓`
  };
};
const START_NOTE_RECORDING_TOOL = {
  type: "function",
  function: {
    name: "start_note_recording",
    description: "Start recording notes when user explicitly asks to record, take notes, or begin recording. Call this immediately without asking for confirmation.",
    parameters: {
      type: "object",
      properties: {
        confirmation_message: {
          type: "string",
          description: "Natural confirmation like 'Sure! Recording now. Say stop when done.'"
        }
      },
      required: ["confirmation_message"]
    }
  }
};

/**
 * Calculate target word count based on duration
 * Average speech rate: ~150 words per minute
 */
/**
 * Calculate target word count based on duration
 * Average speech rate: ~150 words per minute
 */
export const calculateTargetWordCount = (durationMinutes) => {
  const wordsPerMinute = 150;
  const baseWords = durationMinutes * wordsPerMinute;
  return Math.floor(baseWords * 0.85); // 15% reduction for natural flow
};

/**
 * Parse duration from user input
 */
export const parseDuration = (topic) => {
  const durationPatterns = [
    /(\d+)\s*min(?:ute)?s?/i,
    /(\d+)\s*m\b/i,
    /(\d+)[-\s]minute/i
  ];
  
  for (const pattern of durationPatterns) {
    const match = topic.match(pattern);
    if (match) {
      const minutes = parseInt(match[1]);
      return Math.max(1, Math.min(10, minutes)); // Clamp 1-10 minutes
    }
  }
  
  return null;
};

/**
 * Validate and constrain duration
 */
export const validateDuration = (requestedMinutes) => {
  if (!requestedMinutes || requestedMinutes < 1) {
    return { valid: true, minutes: 3, message: null }; // Default 3 minutes
  }
  
  if (requestedMinutes < 1) {
    return { 
      valid: false, 
      minutes: 1, 
      message: "Podcasts must be at least 1 minute long. I'll create a 1-minute episode." 
    };
  }
  
  if (requestedMinutes > 10) {
    return { 
      valid: false, 
      minutes: 10, 
      message: "Podcasts are limited to 10 minutes max. I'll create a 10-minute episode." 
    };
  }
  
  return { valid: true, minutes: requestedMinutes, message: null };
};

/**
 * Check if podcast request is too vague and needs clarification
 */
export const isVaguePodcastRequest = (topic) => {
  if (!topic) return true;
  
  const topicLower = topic.toLowerCase().trim();
  
  // URLs are NOT vague - they're specific content
  if (containsURL(topic)) {
    console.log('✅ URL detected in podcast request - this is specific');
    return false;
  }
  
  // Remove duration-related phrases first
  const cleanedTopic = topicLower
    .replace(/\d+\s*min(?:ute)?s?/gi, '')
    .replace(/\d+\s*m\b/gi, '')
    .replace(/quick/gi, '')
    .replace(/short/gi, '')
    .replace(/long/gi, '')
    .trim();
  
  // Check for vague requests
  const vaguePatterns = [
    /^(my\s+)?daily\s*podcast$/,
    /^(create|make|generate)\s+(a\s+)?podcast$/,
    /^podcast$/,
    /^(my\s+)?morning\s*podcast$/,
    /^(my\s+)?evening\s*podcast$/,
    /^(my\s+)?afternoon\s*podcast$/,
    /^news\s*podcast$/,
    /^today'?s?\s*podcast$/,
    /^podcast\s*(for\s*)?(today|this\s+morning|tonight)$/
  ];
  
  const isVague = vaguePatterns.some(pattern => pattern.test(cleanedTopic));
  
  if (isVague) {
    console.log('⚠️ Vague podcast request detected:', topic);
  }
  
  return isVague;
};

/**
 * Check if text contains a URL
 */
export const containsURL = (text) => {
  if (!text) return false;
  
  const urlPatterns = [
    /https?:\/\/[^\s]+/i,  // http:// or https://
    /www\.[^\s]+/i,         // www.
    /youtube\.com\/watch/i, // YouTube
    /youtu\.be\//i,         // YouTube short
    /[a-z0-9-]+\.(com|org|net|edu|gov|io|co|ai|tech|news|blog|medium)\b/i  // Common TLDs
  ];
  
  return urlPatterns.some(pattern => pattern.test(text));
};

/**
 * Extract URL from text
 */
export const extractURL = (text) => {
  if (!text) return null;
  
  // Try to match full URL with protocol
  const fullURLMatch = text.match(/https?:\/\/[^\s]+/i);
  if (fullURLMatch) {
    return fullURLMatch[0].replace(/[.,;!?]$/, ''); // Remove trailing punctuation
  }
  
  // Try to match www. URLs
  const wwwMatch = text.match(/www\.[^\s]+/i);
  if (wwwMatch) {
    return 'https://' + wwwMatch[0].replace(/[.,;!?]$/, '');
  }
  
  // Try to match domain-only URLs
  const domainMatch = text.match(/\b([a-z0-9-]+\.)+[a-z]{2,}\b/i);
  if (domainMatch) {
    const domain = domainMatch[0];
    // Only add https:// if it looks like a real domain
    if (!domain.includes('@') && domain.split('.').length >= 2) {
      return 'https://' + domain;
    }
  }
  
  return null;
};

/**
 * Check if URL is a YouTube video
 */
export const isYouTubeURL = (url) => {
  if (!url) return false;
  return /youtube\.com\/watch|youtu\.be\/|youtube\.com\/embed/i.test(url);
};

/**
 * Fetch YouTube video metadata using oEmbed API (no API key needed!)
 */
export const fetchYouTubeMetadata = async (videoId) => {
  if (!videoId) return null;
  
  try {
    console.log('🎬 Fetching YouTube metadata for:', videoId);
    
    // Use YouTube oEmbed API - FREE, no key required
    const response = await fetch(
      `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
    );
    
    if (!response.ok) {
      console.error('❌ YouTube oEmbed failed:', response.status);
      return null;
    }
    
    const data = await response.json();
    
    console.log('✅ YouTube metadata fetched:', {
      title: data.title,
      author: data.author_name
    });
    
    return {
      title: data.title,
      author: data.author_name,
      thumbnail: data.thumbnail_url,
      videoId: videoId
    };
    
  } catch (error) {
    console.error('❌ Error fetching YouTube metadata:', error);
    return null;
  }
};

/**
 * Scrape YouTube page for description (backup method)
 */
export const scrapeYouTubeDescription = async (videoId) => {
  try {
    const response = await fetch(`https://www.youtube.com/watch?v=${videoId}`);
    const html = await response.text();
    
    // Extract description from meta tags
    const descMatch = html.match(/<meta name="description" content="([^"]+)"/);
    if (descMatch) {
      return descMatch[1];
    }
    
    return null;
  } catch (error) {
    console.error('Failed to scrape YouTube description:', error);
    return null;
  }
};
/**
 * Randomly determine guest configuration
 * 40% - No guest (2 hosts only)
 * 40% - 1 guest (3 total speakers)
 * 20% - 2 guests (4 total speakers but they alternate)
 */
export const determineGuestConfiguration = () => {
  const random = Math.random();
  
  if (random < 0.4) {
    // No guest - just 2 hosts
    return {
      includeGuest: false,
      startingHosts: ["Host 1", "Host 2"],
      guestSpeaker: null,
      totalSpeakers: 2,
      description: "2 hosts only"
    };
  } else if (random < 0.8) {
    // 1 guest - 3 total speakers
    const allHosts = ["Host 1", "Host 2", "Host 3", "Host 4"];
    const shuffled = allHosts.sort(() => Math.random() - 0.5);
    return {
      includeGuest: true,
      startingHosts: [shuffled[0], shuffled[1]],
      guestSpeaker: shuffled[2],
      totalSpeakers: 3,
      description: `${shuffled[0]} & ${shuffled[1]} start, ${shuffled[2]} joins as guest`
    };
  } else {
    // 2 guests - but they alternate (never all 4 at once)
    const allHosts = ["Host 1", "Host 2", "Host 3", "Host 4"];
    const shuffled = allHosts.sort(() => Math.random() - 0.5);
    return {
      includeGuest: true,
      includeTwoGuests: true,
      startingHosts: [shuffled[0], shuffled[1]],
      guestSpeaker: shuffled[2],
      secondGuest: shuffled[3],
      totalSpeakers: 4,
      description: `${shuffled[0]} & ${shuffled[1]} start, ${shuffled[2]} joins first, then ${shuffled[3]}`
    };
  }
};


/**
 * Get time-appropriate greeting for Inner Loop show
 */
export const getInnerLoopGreeting = (userName) => {
  const hour = new Date().getHours();
  
  if (hour >= 5 && hour < 12) {
    return userName 
      ? `Good morning ${userName}, welcome to your morning Inner Loop.`
      : "Good morning, Welcome to your morning the Inner Loop.";
  } else if (hour >= 12 && hour < 17) {
    return userName
      ? `Hey ${userName}, welcome back to the Inner Loop.`
      : "Hey! Welcome back to the Inner Loop.";
  } else if (hour >= 17 && hour < 21) {
    return userName
      ? `Good evening ${userName}, welcome to your evening Inner Loop.`
      : "Good evening, Welcome to your evening Inner Loop.";
  } else {
    return userName
      ? `Hey ${userName}, thanks for joining us on the Inner Loop.`
      : "Hey! Thanks for joining us on the Inner Loop.";
  }
};

/**
 * Get time-appropriate outro for Inner Loop show
 */
export const getInnerLoopOutro = (userName) => {
  const hour = new Date().getHours();
  
  if (hour >= 5 && hour < 12) {
    return userName
      ? `Have a great day ${userName}. We'll see you next time on the Inner Loop.`
      : "Have a great day. We'll see you next time on the Inner Loop.";
  } else if (hour >= 12 && hour < 17) {
    return userName
      ? `Enjoy the rest of your day ${userName}. Catch you on the next Inner Loop.`
      : "Enjoy the rest of your day. Catch you on the next Inner Loop show.";
  } else if (hour >= 17 && hour < 21) {
    return userName
      ? `Have a wonderful evening ${userName}. We'll see you next time on the Inner Loop.`
      : "Have a wonderful evening. We'll see you next time on the Inner Loop.";
  } else {
    return userName
      ? `Have a great night ${userName}. See you on the next Inner Loop.`
      : "Have a great night. See you on the next Inner Loop show.";
  }
};

// ====================
// UPDATED PODCAST TOOL (for Azurechatbot.js)
// ====================

export const ENHANCED_PODCAST_TOOL = {
  type: "function",
  function: {
    name: "generate_podcast_dialogue",
    description: "Generate a personalized Inner Loop podcast episode (1-10 minutes). Randomly includes 0-2 guests. ONLY call if topic is specific. For vague requests like 'my daily podcast' or just 'podcast', ask what they want it about.",
    parameters: {
      type: "object",
      properties: {
        topic: {
          type: "string",
          description: "SPECIFIC topic for the podcast. Must be detailed, not vague like 'daily podcast' or 'news'. Should describe what the podcast is about."
        },
        user_name: {
          type: "string",
          description: "User's first name for personalized Inner Loop greeting/outro"
        },
        duration_minutes: {
          type: "number",
          description: "Target duration (1-10 minutes). Default: 3 minutes",
          minimum: 1,
          maximum: 10,
          default: 3
        }
      },
      required: ["topic"]
    }
  }
};

// ====================
// ENHANCED SYSTEM MESSAGE INSTRUCTIONS (for Azurechatbot.js)
// ====================

export const PODCAST_VAGUE_REQUEST_INSTRUCTIONS = `
🎙️ VAGUE PODCAST REQUEST DETECTION - CRITICAL:

**YOU MUST CHECK EVERY PODCAST REQUEST BEFORE CALLING THE TOOL**

STEP 1: Analyze the user's message for TOPIC SPECIFICITY

❌ VAGUE/GENERIC (DO NOT CALL TOOL - ASK FOR CLARIFICATION):
These requests lack a SPECIFIC TOPIC:
- "Create my daily podcast"
- "Make a podcast" 
- "Generate my morning podcast"
- "Podcast for today"
- "My afternoon podcast"
- "Quick podcast"
- "Create a news podcast" (too generic - what kind of news?)
- "Make my podcast"
- "Generate podcast"

✅ SPECIFIC TOPIC (OK TO CALL TOOL):
These have CLEAR, SPECIFIC topics:
- "Create a podcast about today's tech news" (topic: tech news)
- "Make a 5 minute podcast on climate change" (topic: climate change)
- "Generate my daily podcast about business headlines" (topic: business headlines)
- "Podcast about Tesla's latest announcement" (topic: Tesla announcement)
- "Morning podcast covering AI developments" (topic: AI developments)
- "Create a podcast about the latest SpaceX launch" (topic: SpaceX launch)

DECISION RULE:
Ask yourself: "Can I identify what the podcast is ABOUT?"
- If NO → ASK for clarification
- If YES → CALL generate_podcast_dialogue

WHEN REQUEST IS VAGUE:
DO NOT call generate_podcast_dialogue!
Instead, respond with:

"I'd love to create your Inner Loop podcast! What topic would you like me to cover? For example:
- Today's tech news
- Business headlines  
- Sports updates
- Science discoveries
- A specific topic you're interested in

Just let me know what you'd like to hear about!"

WAIT for them to specify a topic, THEN call the tool.

WHEN REQUEST IS SPECIFIC:
1. Extract the clear topic
2. Parse any duration mentioned
3. Call generate_podcast_dialogue with topic and duration
`;

export const PODCAST_DURATION_INSTRUCTIONS = `
🎙️ PODCAST DURATION CONTROL - CRITICAL:

DURATION PARSING:
- Detect: "2 minute podcast", "5 min podcast", "quick 1-minute summary"
- Extract number and pass as duration_minutes parameter
- Validate: minimum 1 minute, maximum 10 minutes

DURATION HANDLING:
- < 1 minute: Set to 1 min, say "Podcasts must be at least 1 minute long. Creating a 1-minute Inner Loop episode!"
- > 10 minutes: Set to 10 min, say "Podcasts are limited to 10 minutes max. Creating a 10-minute Inner Loop episode!"
- No duration specified: Use default 3 minutes
- Valid range: 1-10 minutes

EXAMPLES:
User: "create a 2 minute podcast about tech news"
→ Call generate_podcast_dialogue({ topic: "tech news", duration_minutes: 2 })

User: "make me a 30 second podcast about sports"
→ Respond: "Podcasts must be at least 1 minute long. Creating a 1-minute Inner Loop episode!"
→ Call generate_podcast_dialogue({ topic: "sports", duration_minutes: 1 })

User: "15 minute podcast about AI"
→ Respond: "Podcasts are limited to 10 minutes max. Creating a 10-minute Inner Loop episode!"
→ Call generate_podcast_dialogue({ topic: "AI", duration_minutes: 10 })
`;


// ====================
// GENERATE DIALOGUE FUNCTION (for ChatMode.jsx to use)
// ====================

export const generatePodcastDialogueWithDuration = async (
  topic, 
  searchResults, 
  userName,
  durationMinutes = 3,
  azureEndpoint,
  azureDeployment,
  azureKey
) => {
  // Validate Azure config
  if (!azureEndpoint || !azureDeployment || !azureKey) {
    console.error('❌ Missing Azure configuration:', {
      hasEndpoint: !!azureEndpoint,
      hasDeployment: !!azureDeployment,
      hasKey: !!azureKey
    });
    throw new Error('Azure OpenAI configuration is missing');
  }
  
  // Validate duration
  const validation = validateDuration(durationMinutes);
  const targetMinutes = validation.minutes;
  const targetWordCount = calculateTargetWordCount(targetMinutes);
  
  // Randomly determine guest configuration
  const guestConfig = determineGuestConfiguration();
  
  console.log('🎙️ Podcast Generation:', {
    requestedDuration: durationMinutes,
    targetMinutes,
    targetWordCount,
    guestConfiguration: guestConfig.description,
    userName: userName || 'Anonymous',
    endpoint: azureEndpoint,
    deployment: azureDeployment
  });
  
  const { includeGuest, includeTwoGuests, guestSpeaker, secondGuest } = guestConfig;
  const validSpeakers = includeTwoGuests 
    ? [...guestConfig.startingHosts, guestSpeaker, secondGuest]
    : includeGuest 
      ? [...guestConfig.startingHosts, guestSpeaker]
      : guestConfig.startingHosts;
  
  // Get branded greetings
  const innerLoopGreeting = getInnerLoopGreeting(userName);
  const innerLoopOutro = getInnerLoopOutro(userName);
  
  // Build duration guidance
  const durationGuidance = `
CRITICAL DURATION: ${targetMinutes} minute${targetMinutes !== 1 ? 's' : ''} (~${targetWordCount} words)

PACING:
${targetMinutes === 1 ? '- ULTRA BRIEF: 1-2 line intro, 1-2 points, quick outro' 
  : targetMinutes <= 3 ? '- CONCISE: Brief intro, 2-3 points, quick outro'
  : targetMinutes <= 5 ? '- MODERATE: Proper intro, 3-4 points, thoughtful outro'
  : '- EXTENDED: Full intro, 4-5 points, comprehensive outro'}`;

  // Build guest context
  let guestContext = '';
  if (includeGuest) {
    guestContext = `\n=== GUESTS ===\n`;
    guestContext += `Hosts: ${guestConfig.startingHosts.join(' & ')}\n`;
    
    if (includeTwoGuests) {
      guestContext += `Guest 1: ${guestSpeaker} (joins ~30%, leaves when Guest 2 arrives)\n`;
      guestContext += `Guest 2: ${secondGuest} (joins ~60%)\n`;
      guestContext += `NEVER all 4 at once!\n`;
    } else {
      guestContext += `Guest: ${guestSpeaker} (joins ~40%)\n`;
    }
  }
  
  // Build search context
  let searchContext = '';
  if (searchResults && searchResults.length > 0) {
    searchContext = `\n=== NEWS SOURCES ===\n`;
    searchResults.forEach((result, idx) => {
      searchContext += `${idx + 1}. ${result.title}\n   ${result.snippet.substring(0, 100)}...\n`;
    });
  }
  
  const prompt = `Generate ${targetMinutes}-minute Inner Loop podcast about "${topic}".

${durationGuidance}
${guestContext}
${searchContext}

⚠️ NUMBER FORMATTING:
- Write numbers naturally: "$250 million" NOT "250 dollar million"
- Currency: $50, $1.5 billion (dollar sign BEFORE number)
- Percentages: 50%, 25% (percent sign AFTER number)
- Large numbers: 10,000 or 1.5 million (use commas or words)

BRANDED INTRO/OUTRO:
FIRST LINE: "${innerLoopGreeting}"
LAST LINE: "${innerLoopOutro}"

SPEAKERS: ${validSpeakers.join(', ')}

RULES:
1. Target: ~${targetWordCount} words total
2. Each turn: 30-80 words
3. Natural conversation
4. Address listener as "you" (singular)
5. Never use speaker names in dialogue
6. Format numbers correctly (see above)

Return JSON:
[
  {"speaker":"${guestConfig.startingHosts[0]}","line":"${innerLoopGreeting}","emotion":"enthusiastic"},
  ...
  {"speaker":"${guestConfig.startingHosts[0]}","line":"${innerLoopOutro}","emotion":"warm"}
]`;

  try {
    console.log('📡 Calling Azure API:', {
      endpoint: azureEndpoint,
      deployment: azureDeployment
    });
    
    const response = await fetch(
      `${azureEndpoint}/openai/deployments/${azureDeployment}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': azureKey
        },
        body: JSON.stringify({
          messages: [
            {
              role: "system",
              content: `You are a podcast script writer for "Inner Loop" show. Create ${targetMinutes}-minute dialogue with branded greetings.`
            },
            {
              role: "user",
              content: prompt
            }
          ],
          temperature: 0.8,
          max_tokens: Math.min(4000, targetWordCount * 2)
        })
      }
    );

    console.log('📡 Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Azure API error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      throw new Error(`API error: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    const content = data.choices[0]?.message?.content;
    
    if (!content) {
      throw new Error('No content in API response');
    }
    
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (!jsonMatch) {
      console.error('❌ Failed to extract JSON from response:', content.substring(0, 200));
      throw new Error('Failed to parse dialogue JSON');
    }
    
    const dialogue = JSON.parse(jsonMatch[0]);
    
    const totalWords = dialogue.reduce((sum, turn) => 
      sum + turn.line.split(/\s+/).length, 0
    );
    
    console.log('✅ Generated:', {
      targetMinutes,
      actualWords: totalWords,
      turns: dialogue.length,
      estimatedDuration: `${(totalWords / 150).toFixed(1)}min`,
      guests: guestConfig.description
    });
    
    return {
      dialogue,
      metadata: {
        targetDuration: targetMinutes,
        estimatedDuration: totalWords / 150,
        wordCount: totalWords,
        validationMessage: validation.message,
        guestConfiguration: guestConfig.description,
        speakers: validSpeakers
      }
    };
    
  } catch (error) {
    console.error('❌ Dialogue generation error:', error);
    throw error;
  }
};


/**
 * Handle podcast generation with all enhancements
 */
const handlePodcastGeneration = async (toolCallId, args, messageHistory, setMessages, getStoredUserName, sendToolResult, combineAudioSegments) => {
  if (!args || !args.topic) {
    console.error('Invalid args passed to handlePodcastGeneration');
    return;
  }

  const topic = args.topic;
  const userName = args.user_name || getStoredUserName();
  const searchResults = args.searchResults || null;
  
  // Parse and validate duration
  let durationMinutes = args.duration_minutes;
  if (!durationMinutes) {
    durationMinutes = parseDuration(topic);
  }
  
  const validation = validateDuration(durationMinutes);
  const finalDuration = validation.minutes;
  
  console.log('🎙️ Podcast request:', {
    topic,
    requestedDuration: durationMinutes,
    finalDuration,
    userName: userName || 'Anonymous'
  });
  
  const loadingMessageId = Date.now();
  setMessages(prev => [...prev, {
    id: loadingMessageId,
    type: 'bot',
    content: validation.message 
      ? validation.message + '\n\nCreating your Inner Loop podcast...'
      : `Creating your ${finalDuration}-minute Inner Loop podcast...`,
    isLoading: true,
    loadingStage: 'generating'
  }]);
  
  try {
    // Generate dialogue with all enhancements
    const result = await generatePodcastDialogueWithDuration(
      topic, 
      searchResults, 
      null, // startingHosts determined randomly
      userName,
      finalDuration
    );
    
    const { dialogue, metadata } = result;
    
    setMessages(prev => 
      prev.map(msg => 
        msg.id === loadingMessageId 
          ? { 
              ...msg, 
              content: `Generating audio for your ${finalDuration}-minute Inner Loop episode...`, 
              loadingStage: 'processing' 
            }
          : msg
      )
    );
    
    const updateProgress = (progressText, stage) => {
      setMessages(prev => 
        prev.map(msg => 
          msg.id === loadingMessageId 
            ? { ...msg, content: progressText, loadingStage: stage }
            : msg
        )
      );
    };
    
    const audioUrl = await combineAudioSegments(dialogue, updateProgress);
    
    const episodeTitle = topic.replace(/\d+\s*min(?:ute)?s?/gi, '').trim();
    
    const toolResult = {
      podcast_title: episodeTitle,
      duration_minutes: finalDuration,
      estimated_duration: metadata.estimatedDuration,
      word_count: metadata.wordCount,
      guest_configuration: metadata.guestConfiguration,
      message: `Created ${finalDuration}-minute Inner Loop podcast: "${episodeTitle}"`,
      success: true
    };
    
    try {
      await sendToolResult(messageHistory, toolCallId, 'generate_podcast_dialogue', args, toolResult);
    } catch (azureError) {
      console.error('Failed to send result to Azure:', azureError);
    }
    
    setMessages(prev => 
      prev.map(msg => 
        msg.id === loadingMessageId 
          ? { 
              ...msg, 
              content: `Your ${finalDuration}-minute Inner Loop podcast "${episodeTitle}" is ready!`,
              dialogue,
              audioUrl,
              topic: episodeTitle,
              isPodcast: true,
              isLoading: false,
              searchResults,
              podcastTitle: episodeTitle,
              duration: finalDuration,
              metadata
            }
          : msg
      )
    );
    
  } catch (error) {
    console.error('Podcast error:', error);
    setMessages(prev => 
      prev.map(msg => 
        msg.id === loadingMessageId 
          ? { ...msg, content: 'Generation failed. Try again!', isLoading: false }
          : msg
      )
    );
  }
};


// Podcast Tool with Personalization
const PODCAST_TOOL = {
  type: "function",
  function: {
    name: "generate_podcast_dialogue",
    description: "Generate Inner Loop podcast ONLY when user provides SPECIFIC topic. DO NOT call for vague requests like 'my daily podcast' or 'create a podcast' - ask what topic they want first. Topic must be specific (e.g., 'tech news', 'Tesla earnings', 'climate change'), not generic.",
    parameters: {
      type: "object",
      properties: {
        topic: {
          type: "string",
          description: "SPECIFIC, DETAILED topic. NOT vague like 'daily podcast' or 'news'. Must clearly describe what the podcast covers (e.g., 'AI developments', 'sports highlights', 'Tesla announcement')."
        },
        user_name: {
          type: "string",
          description: "User's first name for personalized Inner Loop greeting/outro"
        },
        duration_minutes: {
          type: "number",
          description: "Target duration (1-10 minutes). Default: 3 minutes",
          minimum: 1,
          maximum: 10,
          default: 3
        }
      },
      required: ["topic"]
    }
  }
};

// Scheduler Tool
const SCHEDULER_TOOL = {
  type: "function",
  function: {
    name: "schedule_event",
    description: "Schedule an event or task. Supports RECURRING EVENTS like 'every Friday at 6pm' or 'study Spanish every Friday evening at 6pm'. For recurring events, the system will automatically create instances for the next 8 weeks.",
    parameters: {
      type: "object",
      properties: {
        title: {
          type: "string",
          description: "The event or task title (e.g., 'Study Spanish', 'Team Meeting')"
        },
        date: {
          type: "string",
          description: "Date in format YYYY-MM-DD or relative like 'today', 'tomorrow', 'next monday'. For recurring events, this is the start date."
        },
        time: {
          type: "string",
          description: "Time in format HH:MM (24-hour) or like '6pm', '9:30am'"
        },
        description: {
          type: "string",
          description: "IMPORTANT: Include recurrence info here like 'every Friday' or 'every day'. System will detect and create recurring instances automatically."
        }
      },
      required: ["title", "date", "time"]
    }
  }
};

// Get Schedule Tool
const GET_SCHEDULE_TOOL = {
  type: "function",
  function: {
    name: "get_schedule",
    description: "Retrieve scheduled events for a specific date or date range. Use when user asks about their schedule, what's planned, or what they have coming up.",
    parameters: {
      type: "object",
      properties: {
        date: {
          type: "string",
          description: "Date to query in format YYYY-MM-DD or relative like 'today', 'tomorrow', 'this week'"
        },
        range: {
          type: "string",
          enum: ["day", "week", "month"],
          description: "Time range to query (default: day)"
        }
      },
      required: ["date"]
    }
  }
};

const UPDATE_EVENT_TOOL = {
  type: "function",
  function: {
    name: "update_event",
    description: "Update an existing calendar event. Use when user wants to change event details, rename, reschedule, or move to a different date/time. CRITICAL: You must identify which event to update from the conversation context or recent schedule display.",
    parameters: {
      type: "object",
      properties: {
        eventTitle: {
          type: "string",
          description: "CRITICAL: The CURRENT title of the event to update. Look at the recent schedule display or conversation to find this. If user says 'change it to 8pm', look back at what event was just discussed. If unclear, use the most recently mentioned event."
        },
        newTitle: {
          type: "string",
          description: "Optional new title for the event"
        },
        newDate: {
          type: "string",
          description: "Optional new date in format YYYY-MM-DD or relative like 'today', 'tomorrow', 'next monday'"
        },
        newTime: {
          type: "string",
          description: "Optional new time in format HH:MM (24-hour) or like '8pm', '20:00'"
        },
        newDescription: {
          type: "string",
          description: "Optional new description"
        }
      },
      required: ["eventTitle"]
    }
  }
};

const DELETE_EVENT_TOOL = {
  type: "function",
  function: {
    name: "delete_event",
    description: "Delete an event from the calendar. Use when user wants to remove, cancel, or delete an event.",
    parameters: {
      type: "object",
      properties: {
        eventTitle: {
          type: "string",
          description: "The title of the event to delete (can be partial match)"
        }
      },
      required: ["eventTitle"]
    }
  }
};

// Journal Tool
const JOURNAL_TOOL = {
  type: "function",
  function: {
    name: "save_journal_entry",
    description: "Save a journal entry when user is done sharing their thoughts. ONLY call this function once per journaling session when the conversation naturally concludes.",
    parameters: {
      type: "object",
      properties: {
        title: {
          type: "string",
          description: "Brief, descriptive title for the journal entry (max 60 characters). Make it specific to what they discussed, not generic."
        },
        summary: {
          type: "string",
          description: "Comprehensive summary of the journal conversation in THIRD PERSON: 'You talked about...', 'You mentioned...', 'You felt...'"
        },
        insights: {
          type: "string",
          description: "Key themes, patterns, emotions, and meaningful insights identified throughout the conversation. Highlight recurring topics, growth areas, or important realizations. Be specific about emotions and patterns you noticed."
        }
      },
      required: ["title", "summary"]
    }
  }
};

// Flashcard Tool
const FLASHCARD_TOOL = {
  type: "function",
  function: {
    name: "generate_flashcards",
    description: "Generate flashcards for studying/memorization. ALWAYS ask user how many flashcards they want before calling this. Use when user wants to create study cards, review materials, or memorize concepts.",
    parameters: {
      type: "object",
      properties: {
        topic: {
          type: "string",
          description: "The subject/topic for flashcards"
        },
        difficulty: {
          type: "string",
          enum: ["easy", "medium", "hard"],
          description: "Difficulty level"
        },
        count: {
          type: "number",
          description: "Number of flashcards (default: 5 if not specified)",
          default: 5
        }
      },
      required: ["topic"]
    }
  }
};

// Quiz Tool
const QUIZ_TOOL = {
  type: "function",
  function: {
    name: "generate_quiz",
    description: "Generate a high-quality quiz to test knowledge. Supports multiple subjects including math with properly formatted equations.",
    parameters: {
      type: "object",
      properties: {
        topic: {
          type: "string",
          description: "The subject/topic for the quiz (e.g., 'algebra', 'world history', 'biology', 'calculus')"
        },
        difficulty: {
          type: "string",
          enum: ["easy", "medium", "hard"],
          description: "Difficulty level"
        },
        count: {
          type: "number",
          description: "Number of questions (default: 10)",
          default: 10
        },
        questionType: {
          type: "string",
          enum: ["multiple_choice", "true_false", "mixed", "math_problems"],
          description: "Type of quiz questions. Use 'math_problems' for mathematical questions with equations",
          default: "mixed"
        }
      },
      required: ["topic"]
    }
  }
};

// ====================
// MAIN FUNCTIONS - WITH FIXES
// ====================

/**
 * Send chat message to Azure OpenAI - FIXED WITH BETTER TIMEOUT AND RETRY
 */
export const sendChatMessage = async (
  messageHistory, 
  userMessage, 
  calendarContext = null,
  memoryService = null,
  systemMessage = null,
  enabledTools = [],
  lastScheduleDisplayed = null,
  firestoreService = null  // ✅ ADD THIS PARAMETER
) => {
  try {
    const messageStart = performance.now();
    
    // Validate configuration
    if (!AZURE_OPENAI_ENDPOINT || AZURE_OPENAI_ENDPOINT.includes('YOUR-RESOURCE')) {
      console.error('Azure OpenAI endpoint not configured');
      throw new Error('Service configuration error. Please contact support@preptune.com');
    }
    
    if (!AZURE_OPENAI_KEY || AZURE_OPENAI_KEY.includes('your-api-key')) {
      console.error('Azure OpenAI key not configured');
      throw new Error('Service configuration error. Please contact support@preptune.com');
    }
    
    if (!AZURE_DEPLOYMENT_NAME || AZURE_DEPLOYMENT_NAME.includes('your-deployment')) {
      console.error('Azure deployment name not configured');
      throw new Error('Service configuration error. Please contact support@preptune.com');
    }
// ✅ Skip parallel load entirely if system message already provided
let username = null;
let memoryContext = null;

if (!systemMessage) {
  const parallelLoader = new ParallelDataLoader(
    firestoreService || globalFirestoreService || window.firestoreService,
    memoryService
  );
  const loadStart = performance.now();
  const result = await parallelLoader.loadUserContextInParallel(userMessage);
  username = result.username;
  memoryContext = result.memoryContext;
  console.log(`⚡ Data loading took ${(performance.now() - loadStart).toFixed(0)}ms`);
} else {
  console.log('⚡ Skipping parallel load - system message already provided');
  username = usernameCacheManager.getUsername();
}

// ✅ Build system message with loaded username
let finalSystemMessage = systemMessage || createSystemMessage(
  username,
  null,
  enabledTools && enabledTools.length > 0 ? enabledTools : []
);
console.log('📋 System message created with username:', username || 'Not set');
    // ✅ ADD MEMORY CONTEXT if available
    if (memoryContext && memoryContext.trim()) {
      console.log('🧠 Adding memory context to system message');
      finalSystemMessage = {
        ...finalSystemMessage,
        content: finalSystemMessage.content + '\n\n' + memoryContext
      };
    }

    // Add calendar context if provided
    if (calendarContext && Object.keys(calendarContext).length > 0) {
      const today = new Date();
      const currentMonth = today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      let calendarInfo = `\n\n📋 CURRENT CALENDAR (${currentMonth}):\n`;
      const sortedDates = Object.keys(calendarContext).sort((a, b) => parseInt(a) - parseInt(b));
      
      for (const dateKey of sortedDates) {
        const events = calendarContext[dateKey];
        if (events && events.length > 0) {
          const date = new Date(today.getFullYear(), today.getMonth(), parseInt(dateKey));
          const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' });
          
          calendarInfo += `\n${dateStr}:\n`;
          events.forEach(event => {
            const status = event.completed ? '✓' : '○';
            const timeStr = event.time ? ` at ${event.time}` : '';
            calendarInfo += `  ${status} ${event.text}${timeStr}\n`;
          });
        }
      }
      
      finalSystemMessage = {
        ...finalSystemMessage,
        content: finalSystemMessage.content + calendarInfo + "\n\nUse appropriate tools to manage calendar events."
      };
    }

    // ✅ Filter out duplicate system prompts
    const cleanedMessages = messageHistory.filter((msg, idx) => {
      if (msg.role === 'system') {
        return false;
      }
      if (msg.isLoading || msg.loadingStage) return false;
      if (!msg.content || !msg.content.trim()) return false;
      return true;
    });

    const messages = [
      finalSystemMessage,
      ...cleanedMessages,
      { role: "user", content: userMessage }
    ];

    console.log('📤 Sending to Azure:', {
      messageCount: messages.length,
      lastMessage: userMessage.substring(0, 50),
      userName: username || 'Not set',
      hasCalendarContext: !!calendarContext,
      hasMemoryContext: !!memoryContext,
      totalTime: `${(performance.now() - messageStart).toFixed(0)}ms`
    });

    const testUrl = `${AZURE_OPENAI_ENDPOINT}/openai/deployments/${AZURE_DEPLOYMENT_NAME}/chat/completions?api-version=2024-02-15-preview`;

    // ============================================
    // ✅ FILTER TOOLS BASED ON enabledTools
    // ============================================
    const allowedTools = [
      USER_NAME_TOOL,
      CLEAR_USER_NAME_TOOL,
      SAVE_MEMORY_TOOL,
      START_NOTE_RECORDING_TOOL,
    ];

    if (enabledTools.includes('podcast')) {
      allowedTools.push(PODCAST_TOOL);
    }
    if (enabledTools.includes('journal')) {
      allowedTools.push(JOURNAL_TOOL);
    }
    if (enabledTools.includes('study')) {
      allowedTools.push(FLASHCARD_TOOL);
      allowedTools.push(QUIZ_TOOL);
    }
    if (enabledTools.includes('planner')) {
      allowedTools.push(SCHEDULER_TOOL);
      allowedTools.push(GET_SCHEDULE_TOOL);
      allowedTools.push(UPDATE_EVENT_TOOL);
      allowedTools.push(DELETE_EVENT_TOOL);
    }

    console.log('🔧 TOOLS BEING SENT TO AZURE:', allowedTools.map(t => t.function.name));

    // ✅ Check if using secure API
    if (AZURE_OPENAI_KEY === "SECURE_API") {
      console.log('🔒 Using SecureAPI for chat');
      
      const validMessages = messages.filter(msg => 
        msg && msg.role && msg.content !== undefined
      );

      const data = await SecureAPI.callAzureOpenAI(validMessages, {
        tools: allowedTools && allowedTools.length > 0 ? allowedTools : undefined,
        tool_choice: allowedTools && allowedTools.length > 0 ? "auto" : undefined,
        temperature: 0.6,
        max_tokens: 1500
      });
      
      console.log('✅ Azure response received', {
        finishReason: data.choices[0]?.finish_reason,
        totalTime: `${(performance.now() - messageStart).toFixed(0)}ms`
      });
      
      return data;
    }

    // Fallback: direct API access
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.error('⏱️ Request timeout after 60 seconds');
      controller.abort();
    }, 60000);

    const response = await fetch(
      testUrl,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': AZURE_OPENAI_KEY
        },
        signal: controller.signal,
        body: JSON.stringify({
          messages: messages,
          tools: allowedTools,
          tool_choice: "auto",
          temperature: 0.6,
          max_tokens: 1500
        })
      }
    );
    
    clearTimeout(timeoutId);

    console.log('📡 Response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error?.message || `HTTP ${response.status}`;
      
      console.error('❌ Azure API error:', {
        status: response.status,
        message: errorMessage,
        error: errorData,
        url: testUrl
      });
      
      if (response.status === 401) {
        throw new Error('Authentication error. Please contact support@preptune.com');
      } else if (response.status === 404) {
        throw new Error('Service unavailable. Please contact support@preptune.com');
      } else if (response.status === 429) {
        throw new Error('Service is currently busy. Please wait a moment and try again.');
      } else if (response.status >= 500) {
        throw new Error('Service temporarily unavailable. Please try again in a moment.');
      } else {
        throw new Error('An error occurred. Please try again or contact support@preptune.com');
      }
    }

    const data = await response.json();
    console.log('✅ Azure response received', {
      finishReason: data.choices[0]?.finish_reason,
      hasContent: !!data.choices[0]?.message?.content,
      hasToolCalls: !!data.choices[0]?.message?.tool_calls,
      totalTime: `${(performance.now() - messageStart).toFixed(0)}ms`
    });

    return data;
    
  } catch (error) {
    console.error('❌ Azure OpenAI error:', error);
    console.error('Error name:', error.name);
    console.error('Error message:', error.message);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your internet connection and try again.');
    }
    
    if (error.message.includes('Failed to fetch')) {
      throw new Error('Connection error. Please check your internet connection and try again.');
    }
    
    if (error.message.includes('support@preptune.com') || 
        error.message.includes('try again')) {
      throw error;
    }
    
    throw new Error('An unexpected error occurred. Please try again or contact support@preptune.com');
  }
};
/**
 * Send tool result back to Azure - FIXED
 */
export const sendToolResult = async (messageHistory, toolCallId, functionName, functionArgs, result, calendarContext = null, memoryService = null) => {
  try {
    const userName = getStoredUserName();
    let systemMessage = createSystemMessage(userName);
    
    if (calendarContext && Object.keys(calendarContext).length > 0) {
      const today = new Date();
      const currentMonth = today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      let calendarInfo = `\n\nCURRENT CALENDAR (${currentMonth}):\n`;
      const sortedDates = Object.keys(calendarContext).sort((a, b) => parseInt(a) - parseInt(b));
      
      for (const dateKey of sortedDates) {
        const events = calendarContext[dateKey];
        if (events && events.length > 0) {
          const date = new Date(today.getFullYear(), today.getMonth(), parseInt(dateKey));
          const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' });
          
          calendarInfo += `\n${dateStr}:\n`;
          events.forEach(event => {
            const status = event.completed ? '✓' : '○';
            const timeStr = event.time ? ` at ${event.time}` : '';
            calendarInfo += `  ${status} ${event.text}${timeStr}\n`;
          });
        }
      }
      
      systemMessage = {
        role: "system",
        content: systemMessage.content + calendarInfo
      };
    }
    
    const messages = [
      systemMessage,
      ...messageHistory,
      {
        role: "assistant",
        content: null,
        tool_calls: [{
          id: toolCallId,
          type: "function",
          function: {
            name: functionName,
            arguments: JSON.stringify(functionArgs)
          }
        }]
      },
      {
        role: "tool",
        tool_call_id: toolCallId,
        content: JSON.stringify(result)
      }
    ];

// ✅ Check if using secure API
    if (AZURE_OPENAI_KEY === "SECURE_API") {
      console.log('🔒 Using SecureAPI for tool result');
      
      const data = await SecureAPI.callAzureOpenAI(messages, {
        temperature: 0.7,
        max_tokens: 300
      });
      
      return data;
    }

    // OLD WAY (fallback)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const response = await fetch(
      `${AZURE_OPENAI_ENDPOINT}/openai/deployments/${AZURE_DEPLOYMENT_NAME}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': AZURE_OPENAI_KEY
        },
        signal: controller.signal,
        body: JSON.stringify({
          messages: messages,
          temperature: 0.7,
          max_tokens: 300
        })
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ Error sending tool result:', errorData);
      return null;
    }

    return await response.json();
    
  } catch (error) {
    console.error('❌ Error sending tool result:', error);
    
    if (error.name === 'AbortError') {
      console.error('Tool result request timed out');
    }
    
    return null;
  }
};

/**
 * Parse Azure response - NO CHANGES NEEDED
 */
export const parseAzureResponse = (response) => {
  if (!response?.choices?.[0]) {
    console.error('Invalid Azure response:', response);
    return { type: 'error', error: 'Invalid response' };
  }

  const choice = response.choices[0];
  
  if (choice.finish_reason === 'tool_calls' && choice.message?.tool_calls) {
    const toolCall = choice.message.tool_calls[0];
    
    try {
      return {
        type: 'tool_call',
        toolCallId: toolCall.id,
        functionName: toolCall.function.name,
        arguments: JSON.parse(toolCall.function.arguments)
      };
    } catch (e) {
      console.error('Failed to parse tool args:', e);
      return { type: 'error', error: 'Parse error' };
    }
  }
  
  if (choice.message?.content) {
    return {
      type: 'message',
      content: choice.message.content
    };
  }
  
  return { type: 'unknown', raw: response };
};

/**
 * Convert messages to Azure format - NO CHANGES NEEDED
 */
export const convertMessagesToAzureFormat = (messages) => {
  return messages
    .filter(msg => {
      if (msg.isLoading || msg.loadingStage) return false;
      if (msg.content?.includes('Creating your podcast...')) return false;
      if (msg.content?.includes('Generating audio')) return false;
      if (msg.content?.includes('Saving your journal')) return false;
      if (msg.content?.includes('Hang tight')) return false;
      if (msg.content?.includes('Creating flashcards...')) return false;
      if (msg.content?.includes('Adding to your calendar...')) return false;
      if (msg.content?.includes('Checking your schedule...')) return false;
      if (msg.content?.includes('Updating your event...')) return false;
      if (msg.content?.includes('Removing from your calendar...')) return false;
      
      return msg.content && msg.content.trim();
    })
    .map(msg => ({
      role: msg.type === 'user' ? 'user' : 'assistant',
      content: msg.content
    }));
};

// ====================
// HELPER FUNCTIONS
// ====================

/**
 * Format dates for podcast topic (for AI to use in dialogue)
 */
export const formatPodcastDates = (searchResults) => {
  if (!searchResults || searchResults.length === 0) return '';
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const mentionedDates = {
    today: false,
    yesterday: false,
    thisWeek: {},
    lastWeek: {}
  };
  
  const formattedDates = searchResults.map(result => {
    const articleDate = new Date(result.datePublished);
    articleDate.setHours(0, 0, 0, 0);
    
    const diffTime = today.getTime() - articleDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    const dayOfWeek = articleDate.toLocaleDateString('en-US', { weekday: 'long' });
    const dateStr = articleDate.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric' 
    });
    
    let datePrefix;
    
    if (diffDays === 0) {
      datePrefix = !mentionedDates.today ? `today ${dayOfWeek}, ${dateStr}` : 'today';
      mentionedDates.today = true;
    } else if (diffDays === 1) {
      datePrefix = !mentionedDates.yesterday ? `yesterday ${dayOfWeek}, ${dateStr}` : 'yesterday';
      mentionedDates.yesterday = true;
    } else if (diffDays >= 2 && diffDays <= 6) {
      datePrefix = !mentionedDates.thisWeek[dayOfWeek] ? `on ${dayOfWeek}, ${dateStr}` : `on ${dayOfWeek}`;
      mentionedDates.thisWeek[dayOfWeek] = true;
    } else if (diffDays >= 7 && diffDays <= 13) {
      datePrefix = !mentionedDates.lastWeek[dayOfWeek] ? `last ${dayOfWeek}, ${dateStr}` : `last ${dayOfWeek}`;
      mentionedDates.lastWeek[dayOfWeek] = true;
    } else {
      datePrefix = `on ${dayOfWeek}, ${dateStr}`;
    }
    
    return `${datePrefix}|${result.title}`;
  });
  
  return `(DATES: ${formattedDates.join(', ')})`;
};

/**
 * Extract search query from podcast topic
 */
export const extractSearchQueryFromTopic = (topic) => {
  if (!topic) return 'news';
  
  const lowerTopic = topic.toLowerCase();
  
  console.log('🔍 Original topic:', topic);
  
  let query = lowerTopic
    .replace(/create\s+a\s+podcast\s+about/gi, '')
    .replace(/make\s+a\s+podcast\s+about/gi, '')
    .replace(/generate\s+a\s+podcast\s+about/gi, '')
    .replace(/podcast\s+about/gi, '')
    .replace(/make\s+me\s+a\s+podcast\s+on/gi, '')
    .replace(/give\s+me\s+a\s+podcast/gi, '')
    .replace(/i\s+want\s+a\s+podcast/gi, '')
    .trim();
  
  console.log('🔍 After podcast phrase removal:', query);
  
  query = query
    .replace(/from\s+last\s+week/gi, '')
    .replace(/from\s+this\s+week/gi, '')
    .replace(/from\s+the\s+past/gi, '')
    .replace(/in\s+the\s+last/gi, '')
    .replace(/\d+\s+weeks?/gi, '')
    .replace(/\d+\s+days?/gi, '')
    .replace(/this\s+week/gi, '')
    .replace(/last\s+week/gi, '')
    .trim();
  
  console.log('🔍 After time phrase removal:', query);
  
  query = query
    .replace(/top\s+\d+/gi, '')
    .replace(/the\s+\d+/gi, '')
    .replace(/\d+\s+stories/gi, '')
    .trim();
  
  console.log('🔍 After number phrase removal:', query);
  
  query = query
    .replace(/latest/gi, '')
    .replace(/recent/gi, '')
    .trim();
  
  console.log('🔍 After generic word removal:', query);
  
  if (query.length < 3) {
    console.log('⚠️ Query too short, extracting topics...');
    
    const topicKeywords = [
      'tech', 'technology', 'business', 'sports', 'gaming', 'games',
      'science', 'health', 'politics', 'entertainment', 'world',
      'finance', 'stock', 'crypto', 'ai', 'artificial intelligence'
    ];
    
    const foundTopics = topicKeywords.filter(keyword => 
      lowerTopic.includes(keyword)
    );
    
    if (foundTopics.length > 0) {
      query = foundTopics.join(' and ') + ' news';
      console.log('✅ Extracted topics:', query);
    } else {
      query = 'news';
      console.log('✅ Using default: news');
    }
  } else {
    query = query.replace(/\s+/g, ' ').trim();
    
    if (!query.match(/news|update|headline|announcement|report/i)) {
      query = query + ' news';
    }
  }
  
  console.log('✅ Final search query:', query);
  return query;
};

/**
 * Extract search query from message
 */
export const extractSearchQuery = (message) => {
  let query = message.toLowerCase()
    .replace(/^(what's|what is|tell me about|show me|find|search for|get me|give me)\s+/i, '')
    .replace(/\?$/, '')
    .trim();
  
  return query;
};

/**
 * Check if this is a new topic (not a follow-up)
 */
export const isNewTopicDetected = (message, lastSearchQuery) => {
  if (!lastSearchQuery) return true;
  
  const messageLower = message.toLowerCase();
  const lastQueryLower = lastSearchQuery.toLowerCase();
  
  const messageTerms = messageLower.split(' ').filter(w => w.length > 3);
  const lastQueryTerms = lastQueryLower.split(' ').filter(w => w.length > 3);
  
  const overlap = messageTerms.filter(term => lastQueryTerms.includes(term));
  const overlapPercentage = overlap.length / Math.max(messageTerms.length, 1);
  
  return overlapPercentage < 0.3;
};

/**
 * Get appropriate search sources based on query
 */
export const getSearchSourcesForQuery = (query) => {
  const lowerQuery = query.toLowerCase();
  
  if (lowerQuery === 'news' || lowerQuery.length < 5) {
    return {
      useGeneralNews: true,
      categories: ['technology', 'business', 'world']
    };
  }
  
  const sources = {
    useGeneralNews: false,
    categories: []
  };
  
  if (lowerQuery.match(/tech|ai|software|hardware|computer|gadget|phone|apple|google|microsoft|tesla/i)) {
    sources.categories.push('technology');
  }
  
  if (lowerQuery.match(/business|stock|market|economy|finance|crypto|bitcoin/i)) {
    sources.categories.push('business');
  }
  
  if (lowerQuery.match(/game|gaming|gta|playstation|xbox|nintendo|esports/i)) {
    sources.categories.push('gaming');
  }
  
  if (lowerQuery.match(/science|health|medical|covid|vaccine|research|study/i)) {
    sources.categories.push('science', 'health');
  }
  
  if (lowerQuery.match(/sport|football|basketball|baseball|soccer|nfl|nba/i)) {
    sources.categories.push('sports');
  }
  
  if (sources.categories.length === 0) {
    sources.useGeneralNews = true;
    sources.categories.push('world');
  }
  
  return sources;
};

/**
 * Check if topic needs a web search
 */
export const topicNeedsSearch = (topic) => {
  if (!topic) return false;
  
  const topicLower = topic.toLowerCase();
  
  const weatherKeywords = [
    'weather', 'temperature', 'forecast', 'rain', 'sunny', 
    'cloudy', 'storm', 'snow', 'wind', 'humidity'
  ];
  if (weatherKeywords.some(keyword => topicLower.includes(keyword))) {
    console.log('🌤️ Weather query detected - will use get_weather tool');
    return false;
  }
  
  const generalTopics = [
    'how to', 'what is', 'explain', 'tutorial', 'guide',
    'tips for', 'ways to', 'benefits of', 'history of'
  ];
  const isGeneralKnowledge = generalTopics.some(keyword => topicLower.includes(keyword));
  
  const newsKeywords = [
    'news', 'latest', 'recent', 'today', 'yesterday',
    'current', 'update', 'breaking', 'announce', 'announcement',
    'stock', 'market', 'launch', 'released', 'event'
  ];
  const hasNewsKeyword = newsKeywords.some(keyword => topicLower.includes(keyword));
  
  const currentEventTerms = [
    'tesla', 'apple', 'google', 'microsoft', 'amazon', 'meta',
    'nvidia', 'amd', 'intel', 'spacex', 'openai', 'anthropic'
  ];
  const hasCurrentEventTerm = currentEventTerms.some(term => topicLower.includes(term));
  
  const timeIndicators = [
    '2024', '2025', 'october', 'november', 'december',
    'this year', 'right now', 'currently'
  ];
  const hasTimeIndicator = timeIndicators.some(indicator => topicLower.includes(indicator));
  
  if (isGeneralKnowledge && !hasNewsKeyword && !hasCurrentEventTerm) {
    console.log('📚 General knowledge topic - no search needed');
    return false;
  }
  
  if (hasNewsKeyword || hasCurrentEventTerm || hasTimeIndicator) {
    console.log('🔍 Current events topic - will search for latest info');
    return true;
  }
  
  console.log('📝 General topic - no search needed');
  return false;
};

/**
 * Clean topic for "ready" message (remove date details)
 */
export const cleanTopicForReadyMessage = (topic) => {
  return topic.replace(/\s*\([^)]*\)/g, '').trim();
};

/**
 * Helper: Detect if topic is math-related
 */
export const shouldUseMathFormat = (topic) => {
  const mathKeywords = [
    'math', 'algebra', 'calculus', 'geometry', 'trigonometry',
    'statistics', 'equation', 'derivative', 'integral',
    'function', 'polynomial', 'fraction', 'probability'
  ];
  
  return mathKeywords.some(keyword => 
    topic.toLowerCase().includes(keyword)
  );
};

/**
 * Generate quiz using AI - WITH ERROR HANDLING
 */
export const generateQuiz = async (topic, difficulty, count, questionType) => {
  console.log('🎯 Generating AI quiz:', { topic, difficulty, count, questionType });
  
  const isMathTopic = shouldUseMathFormat(topic);
  
  const isRegexTopic = topic.toLowerCase().includes('regular expression') || 
                       topic.toLowerCase().includes('regex') ||
                       topic.toLowerCase().includes('formal language');
  
  let specialInstructions = '';
  
  if (isRegexTopic) {
    specialInstructions = `
CRITICAL - REGEX QUESTION FORMAT:
Each question MUST follow this exact pattern:
1. Question describes a LANGUAGE (set of strings with a property)
2. Options are 4 DIFFERENT REGULAR EXPRESSIONS
3. User picks which regex matches the language

EXAMPLE:
{
  "question": "Which regular expression matches L = {x ∈ {0,1}* | x has the same first and last symbol}?",
  "options": [
    "0(0|1)*0 | 1(0|1)*1 | 0 | 1",
    "(0|1)*",
    "0*1*0* | 1*0*1*",
    "(01)* | (10)*"
  ],
  "correctAnswer": "0(0|1)*0 | 1(0|1)*1 | 0 | 1",
  "explanation": "..."
}`;
  } else if (isMathTopic) {
    specialInstructions = `
MATH FORMATTING:
- Use LaTeX notation for ALL mathematical expressions
- Inline math: \\(x^2 + 5\\)
- Display math: \\[\\int_0^1 x^2 dx\\]
- Format ALL options mathematically: \\(x = 4\\) not just "4"

EXAMPLE:
{
  "question": "Solve for x: \\(2x + 5 = 13\\)",
  "options": ["\\(x = 4\\)", "\\(x = 9\\)", "\\(x = -4\\)", "\\(x = 6.5\\)"],
  "correctAnswer": "\\(x = 4\\)",
  "explanation": "..."
}`;
  }
  
  const prompt = `Generate a ${difficulty} difficulty quiz about "${topic}" with ${count} questions.

${specialInstructions}

QUALITY REQUIREMENTS:
- Create thoughtful, challenging questions
- ALL options must be plausible and realistic
- NO joke answers, NO "none of the above"
- Include clear explanations

Return ONLY valid JSON array:
[
  {
    "id": 1,
    "type": "multiple_choice",
    "question": "Question text",
    "options": ["Option 1", "Option 2", "Option 3", "Option 4"],
    "correctAnswer": "Option 1",
    "explanation": "Why this is correct..."
  }
]`;

  try {
// ✅ Check if using secure API
    if (AZURE_OPENAI_KEY === "SECURE_API") {
      console.log('🔒 Using SecureAPI for quiz generation');
      
      const data = await SecureAPI.callAzureOpenAI([
        {
          role: "system",
          content: "You are an expert educational content creator. Generate high-quality quiz questions. Always return valid JSON arrays only."
        },
        {
          role: "user",
          content: prompt
        }
      ], {
        temperature: 0.8,
        max_tokens: 3000
      });
      
      const content = data.choices[0]?.message?.content;
      console.log('📝 Raw AI response:', content?.substring(0, 200));
      
      const jsonMatch = content.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const quiz = JSON.parse(jsonMatch[0]);
        // ... rest of validation code stays the same
      }
    }

    // OLD WAY (fallback)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const response = await fetch(
      `${AZURE_OPENAI_ENDPOINT}/openai/deployments/${AZURE_DEPLOYMENT_NAME}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': AZURE_OPENAI_KEY
        },
        signal: controller.signal,
        body: JSON.stringify({
          messages: [
            {
              role: "system",
              content: "You are an expert educational content creator. Generate high-quality quiz questions. Always return valid JSON arrays only."
            },
            {
              role: "user",
              content: prompt
            }
          ],
          temperature: 0.8,
          max_tokens: 3000
        })
      }
    );

    clearTimeout(timeoutId);
    if (!response.ok) {
      throw new Error(`Azure API error: ${response.status}`);
    }

    const data = await response.json();
    const content = data.choices[0]?.message?.content;
    
    console.log('📝 Raw AI response:', content?.substring(0, 200));
    
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      const quiz = JSON.parse(jsonMatch[0]);
      
      const validatedQuiz = quiz.map((q, idx) => ({
        id: q.id || idx + 1,
        type: q.type || 'multiple_choice',
        question: q.question,
        ...(q.type === 'multiple_choice' ? {
          options: q.options || [],
          correctAnswer: q.correctAnswer
        } : {
          correctAnswer: q.correctAnswer
        }),
        explanation: q.explanation || 'No explanation provided.'
      }));
      
      console.log('✅ Generated quiz:', {
        count: validatedQuiz.length,
        types: validatedQuiz.map(q => q.type)
      });
      
      return validatedQuiz;
    }
    
    throw new Error('Failed to parse quiz JSON');
    
  } catch (error) {
    console.error('❌ Quiz generation error:', error);
    
    if (isRegexTopic) {
      console.log('⚠️ Using REGEX fallback quiz generator');
      return generateRegexFallbackQuiz(count, difficulty);
    }
    
    console.log('⚠️ Using standard fallback quiz generator');
    return generateFallbackQuiz(topic, count, questionType, difficulty);
  }
};
const CLEAR_USER_NAME_TOOL = {
  type: "function",
  function: {
    name: "clear_user_name",
    description: "Clear/remove the user's saved name when they ask to remove it, forget it, stop using it, or don't want to be called by name anymore. Use when user says things like: 'remove my name', 'forget my name', 'don't call me by my name', 'stop using my name', etc.",
    parameters: {
      type: "object",
      properties: {
        confirmation: {
          type: "string",
          description: "Natural confirmation message like 'Sure, I've removed your name'"
        }
      },
      required: ["confirmation"]
    }
  }
};
/**
 * Fallback quiz generators
 *//**
 * Fallback quiz generators
 */
const generateRegexFallbackQuiz = (count, difficulty) => {
  const regexQuestions = [
    {
      question: "Which regular expression matches L = {x ∈ {0,1}* | x has the same first and last symbol}?",
      options: [
        "0(0|1)*0 | 1(0|1)*1 | 0 | 1",
        "(0|1)*",
        "0*1*0* | 1*0*1*",
        "(01)* | (10)*"
      ],
      correctAnswer: "0(0|1)*0 | 1(0|1)*1 | 0 | 1",
      explanation: "The regex must start with 0 and end with 0, OR start with 1 and end with 1."
    },
    {
      question: "Which regular expression matches L = {x ∈ {0,1}* | x contains the substring '101'}?",
      options: [
        "(0|1)*101(0|1)*",
        "101*",
        "1*0*1*",
        "101 | (01)*"
      ],
      correctAnswer: "(0|1)*101(0|1)*",
      explanation: "The pattern allows any characters before and after 101."
    }
  ];
  
  return regexQuestions.slice(0, Math.min(count, regexQuestions.length)).map((q, idx) => ({
    id: idx + 1,
    type: 'multiple_choice',
    question: q.question,
    options: q.options,
    correctAnswer: q.correctAnswer,
    explanation: q.explanation
  }));
};

const generateFallbackQuiz = (topic, count, questionType, difficulty) => {
  const quiz = [];
  
  for (let i = 0; i < Math.min(count, 5); i++) {
    quiz.push({
      id: i + 1,
      type: 'multiple_choice',
      question: `What is a fundamental concept in ${topic}?`,
      options: [
        `A core principle that defines ${topic}`,
        `An outdated theory about ${topic}`,
        `A minor detail in ${topic}`,
        `An unrelated concept`
      ],
      correctAnswer: `A core principle that defines ${topic}`,
      explanation: `Understanding fundamental concepts is essential for studying ${topic}.`
    });
  }
  
  return quiz;
};
// ✅ INITIALIZATION - Call on app startup
export const initializeOptimizedSystem = async (firestoreService, memoryService) => {
  try {
    console.log('🚀 Initializing Optimized System...');

    // Load and cache username on startup
    await usernameCacheManager.loadAndCache(firestoreService);

    // Subscribe to changes if needed
    usernameCacheManager.subscribe((event, data) => {
      console.log('📢 Username event:', event, data);
    });

    console.log('✅ System initialized!');
    console.log('📊 Cache status:', usernameCacheManager.getCacheStatus());

    return usernameCacheManager;

  } catch (error) {
    console.error('❌ Initialization error:', error);
    return null;
  }
};

// ✅ Export cache manager
export { usernameCacheManager, ParallelDataLoader };
// ====================
// EXPORTS
// ====================

export default {
  // Name management
  getStoredUserName,
  saveUserName,
  clearUserName,
  needsUserName,
  
  // Core functions
  sendChatMessage,
  sendToolResult,
  parseAzureResponse,
  convertMessagesToAzureFormat,
  createSystemMessage,
  // Tools
  USER_NAME_TOOL,
  JOURNAL_TOOL,
  FLASHCARD_TOOL,
  QUIZ_TOOL,
  SCHEDULER_TOOL,
  GET_SCHEDULE_TOOL,
  UPDATE_EVENT_TOOL,
    NATURAL_FOLLOW_UP_INSTRUCTIONS ,
  DELETE_EVENT_TOOL,
    PODCAST_DURATION_INSTRUCTIONS,
  
    // Helper functions
  formatPodcastDates,
  cleanTopicForReadyMessage,
  extractSearchQueryFromTopic,
  topicNeedsSearch,
  extractSearchQuery,
  isNewTopicDetected,
  getSearchSourcesForQuery,
  shouldUseMathFormat,
  generateQuiz,
  

  //podcast
    PODCAST_TOOL,
    generatePodcastDialogueWithDuration,
  handlePodcastGeneration,
  parseDuration,
  validateDuration,
  calculateTargetWordCount,
  determineGuestConfiguration,
  getInnerLoopGreeting,
  getInnerLoopOutro,
    isYouTubeURL,
START_NOTE_RECORDING_TOOL,

  // Constants
  PODCAST_DATE_RULES,
  PODCAST_READY_MESSAGE_RULE,
  FLASHCARD_INSTRUCTIONS,
  QUIZ_GENERATION_INSTRUCTIONS,

  createUpdatedSystemMessage,
};
export const createSystemMessageWithVibe = (userName, vibeContext) => {
  return createSystemMessage(userName, vibeContext);
};